/**
 * SoleadLab 2.0 - Project Store
 *
 * Manages project data including indications, overlays, orthophotos,
 * flight paths, geo photos, and video strips.
 */

import { create } from 'zustand';
import { useMonitoringStore } from './monitoringStore';
import { useMapStore } from './mapStore';
import { useShareSessionStore } from './shareSessionStore';
import { useAuthStore } from './authStore';
import type { MapState, LayerVisibility, LayoutLabelLevelVisibility } from './mapStore';
import { useTaskStore } from './taskStore';
import { useIVStore } from './ivStore';
import { useAssetNodeStore } from './assetNodeStore';
import { useEvidenceStore } from './evidenceStore';
import { useObservationStore } from './observationStore';
import { useFindingStore } from './findingStore';
import { useClaimStore } from './claimStore';
import { useStateStore } from './stateStore';
import { useScoringStore } from './scoringStore';
import { useTruthStore } from './truthStore';
import { scheduleAssetDeletion } from '../services/assetApi';
import { captureDay } from '../utils/captureDate';
import { djiCaptureMs } from '../utils/driveImport';
import { fetchProject } from '../services/projectApi';
import {
  fetchEntityIndications,
  createEntityIndication,
  bulkCreateEntityIndications,
  updateEntityIndication,
  updateEntityIndicationStatus,
  updateEntityIndicationFieldNotes,
  addEntityIndicationFieldPhoto,
  removeEntityIndicationFieldPhoto,
  updateEntityIndicationFieldGeometry,
  deleteEntityIndication,
  fetchSingleEntityIndication,
  isConflictError,
} from '../services/indicationApi';
import {
  fetchEntityOverlays,
  fetchEntityOrthophotos,
  createEntityOverlay,
  updateEntityOverlay,
  deleteEntityOverlay,
  fetchSingleEntityOverlay,
  createEntityOrthophoto,
  updateEntityOrthophoto,
  deleteEntityOrthophoto,
  fetchSingleEntityOrthophoto,
  isSpatialConflictError,
} from '../services/spatialEntityApi';
import { removeSourceFile } from '../utils/sourceFiles';
import { fetchEntityTasksConfig } from '../services/taskEntityApi';
import type { TasksConfig } from '../types/tasks';
import type { ProjectMonitoringConfig, ParsedSchema } from '../types/monitoring';
import type { SLDDiagram } from './monitoringStore';
import type { SiteLayout, AnchorPair, LayoutTextEntity, LayoutTransform, TopologyOverride, ClassificationPattern, ModuleGridConfig, StringOverride } from '../domain/layout/types';
import { pageImageCorners } from '../domain/layout/transform';
import { CLASSIFIER_VERSION } from '../domain/layout/classify';
import { reclassifyAndBind } from '../domain/layout/parentBind';

/**
 * Indication types with their display properties
 * Matches original SoleadLab editor categories
 */
export const INDICATION_TYPES = {
  // Defect types (counted in Total)
  'Diode': { label: 'Diode', color: '#ff0000', counted: true },
  'Hot spots': { label: 'Hot spots', color: '#ffd400', counted: true },
  'Crack': { label: 'Crack', color: '#7ecbff', counted: true },
  'String OC': { label: 'String OC', color: '#00a651', counted: true },
  'Glass': { label: 'Glass', color: '#ff8c00', counted: true },
  'PID': { label: 'PID', color: '#0047ab', counted: true },
  'O.circuit': { label: 'O.circuit', color: '#00a651', counted: true },
  'Opt.OC': { label: 'Opt.OC', color: '#00a651', counted: true },
  'Opt.SC': { label: 'Opt.SC', color: '#20c997', counted: true },
  'Riso/Pol': { label: 'Riso/Pol', color: '#7d3cff', counted: true },
  'Soiling': { label: 'Soiling', color: '#8b4513', counted: true },
  'EL': { label: 'EL', color: '#666666', counted: true },
  'Tracker': { label: 'Tracker', color: '#ec4899', counted: true },
  'Vegetation': { label: 'Vegetation', color: '#84cc16', counted: true },
  // User-defined type. The display name lives in properties.customTypeName;
  // all 'Custom' indications share this single legend/filter row + count.
  'Custom': { label: 'Custom', color: '#64748b', counted: true },
  // Non-counted types (documentation/reference)
  'IV Curve': { label: 'IV Curve', color: '#ffffff', counted: false },
  'IR Comp.': { label: 'IR Comp.', color: '#ffffff', counted: false },
  'Video': { label: 'Video', color: '#000000', counted: false },
  'Photo': { label: 'Photo', color: '#ff00ff', counted: false },
  'DataPoint': { label: 'DataPoint', color: '#10b981', counted: false },
} as const;

export type IndicationType = keyof typeof INDICATION_TYPES;

export type IndicationStatus = 'open' | 'in_progress' | 'resolved';

/** Display labels for the resolution statuses (shared by popup + batch UI). */
export const STATUS_LABELS: Record<IndicationStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
};

/** Canonical status ordering for status pickers/toggles. */
export const STATUS_ORDER: readonly IndicationStatus[] = ['open', 'in_progress', 'resolved'];

/** One server-stamped resolution-status transition (Phase 1). */
export interface IndicationStatusEvent {
  status: IndicationStatus;
  at: string;          // ISO timestamp, stamped server-side
  by: string | null;   // actor userId, stamped server-side (null for field-link writes)
  note?: string;
  // FL-1 field-link attribution — stamped server-side, never client-supplied.
  via?: 'field';
  linkId?: string;
  linkLabel?: string | null;
  actor?: string;      // technician's self-reported name
}

/**
 * One inspection's evidence for a persistent fault (cross-inspection history).
 * A fault seen across several inspections carries one observation per inspection;
 * the top-level image/captureDate/thermalData mirror the LATEST observation.
 */
export interface IndicationObservation {
  /** captureDate (scan day) of this observation. */
  date?: string;
  /** Source Photo indication id this observation's imagery resolves through. */
  sourcePhotoId?: string;
  /** Direct image path, when not sourced from a Photo. */
  image?: string;
  visualImage?: string;
  zoomImage?: string;
  thermalData?: import('../components/editor/thermal/thermalTypes').ThermalAnalysisData;
  /** Full capture timestamp when available. */
  capturedAt?: string;
}

/**
 * Indication feature (GeoJSON-based)
 */
export interface IndicationProperties {
  title?: string;
  description?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  captureDate?: string;
  // Location identifiers (matching original editor)
  station?: string;
  inverter?: string;
  mppt?: string;
  dccb?: string;
  string?: string;
  module?: string;
  /** Per-field origin of the topology values ('derived' | 'human') + the
   *  derive that last wrote here — lets 'derived' values be re-derived when
   *  a grid improves while human-typed values are never displaced (owner
   *  decision 2026-09-02). Shape: domain/topology/applyDerivedTopology. */
  topologyProvenance?: import('../domain/topology/applyDerivedTopology').TopologyProvenance;
  // Media
  photos?: string[];
  linkLibrary?: string;
  urlOverride?: string;
  srtUrl?: string;
  // Photo indication fields (matching original editor)
  /** Relative path to photo blob, e.g. "photos/IMG_001.JPG" */
  image?: string;
  /** Free-text notes (GPS metadata for Photo type, user notes for others) */
  notes?: string;
  // Thermal analysis (DJI R-JPEG client-side parsed)
  thermalData?: import('../components/editor/thermal/thermalTypes').ThermalAnalysisData;
  // Visual/zoom photo pairs (Google Drive URLs)
  /** Google Drive thumbnail URL for visual pair (_V photo) */
  visualImage?: string;
  /** Google Drive thumbnail URL for zoom pair (_Z photo) */
  zoomImage?: string;
  /** Google Drive file ID for visual photo (for constructing different size URLs) */
  visualDriveId?: string;
  /** Google Drive file ID for zoom photo */
  zoomDriveId?: string;
  /** Google Drive file ID of the primary (thermal/anchor) photo — dedup key
   *  so a re-import of the same Drive folder can skip it (P1 undo/history). */
  driveFileId?: string;
  /** Role of the PRIMARY `image`: 'visual' when the photo set had no thermal
   *  (visual/zoom-anchored import), so viewers show it in the visual slot
   *  instead of the thermal one. Absent = thermal (all pre-existing data). */
  imageRole?: 'visual';
  // Task integration
  taskId?: string;
  createIssue?: boolean;
  // Multi-fault classification (PFC-4B) — child faults created from a source Photo
  /** The source Photo indication id this child fault was created from. */
  sourcePhotoId?: string;
  /** Source Photo container marked done in the Classify Photos panel (leaves the queue). */
  reviewed?: boolean;
  // Rotation (degrees) - stored for delta rotation calculations
  rotationDeg?: number;
  /** Fault-detection provenance (P2a): id of the ortho layer that was active
   *  when the mark was created — exemplar scoping for "Find similar" scans.
   *  Absent on marks created with no ortho layer visible (or pre-P2a). */
  orthoLayerId?: string;
  // Custom topology fields from label schema (e.g., "PV", "Block")
  customFields?: Record<string, string>;
  // Custom type display name — only meaningful when type === 'Custom'. Lets the
  // user label an otherwise-generic 'Custom' indication (e.g. "Fence damage").
  customTypeName?: string;
  // EXIF metadata for photo registration
  bearing?: number | null;
  relativeAltitude?: number | null;
  altitude?: number | null;
  gimbalPitch?: number | null;   // DJI GimbalPitchDegree (e.g., -62.8)
  gimbalYaw?: number | null;     // DJI GimbalYawDegree (e.g., -1.1)
  gimbalRoll?: number | null;    // DJI GimbalRollDegree
  flightYaw?: number | null;     // DJI FlightYawDegree (drone body heading)
  exifData?: Record<string, unknown>;
  // Timestamps
  createdAt: string;
  updatedAt: string;
  // Resolution lifecycle (Phase 1). Absent status ⇒ treated as 'open'.
  // statusEvents is an append-only, server-stamped log of transitions.
  status?: IndicationStatus;
  statusEvents?: IndicationStatusEvent[];
  /** FL-1: server-stamped on every field-link write (notes or status).
   *  Powers the "Field changes" review lens. Never written client-side. */
  lastFieldChangeAt?: string;
  /** FL-2: server-stamped provenance on field-created indications. The
   *  linkId is the ownership key for FL-2.5 lifecycle rights (a field link
   *  may reshape/delete only what it created). Never written client-side. */
  createdVia?: {
    via: 'field';
    linkId: string;
    linkLabel: string | null;
    actor: string;
    at: string;
  };
  // Cross-inspection history: one observation per inspection the fault was seen
  // in. Absent ⇒ a single derived observation from the current top-level fields
  // (lazy; see utils/observations.ts). Top-level fields mirror the LATEST.
  observations?: IndicationObservation[];
  /** FL-3a: field-photo attachments, append-only, SERVER-stamped (like
   *  createdVia — never written client-side). Separate from `image` (the
   *  classify/source photo): field photos never become the display image.
   *  entry.key is an R2 key under sites/{siteId}/field/ — render via
   *  buildAssetUrl(key); entry.linkId gates own-photo removal. */
  fieldPhotos?: FieldPhotoEntry[];
}

/** FL-3a: one field-photo attachment (see IndicationProperties.fieldPhotos). */
export interface FieldPhotoEntry {
  id: string;
  key: string;
  at: string;
  via: 'field';
  linkId: string;
  linkLabel: string | null;
  actor: string;
  size: number;
  contentType: string;
}

export interface Indication {
  id: string;
  type: IndicationType;
  geometry: GeoJSON.Geometry;
  properties: IndicationProperties;
  /** Entity table version for optimistic concurrency (V4) */
  _version?: number;
}

/**
 * Update type for indications - allows partial properties updates
 */
export interface IndicationUpdate {
  type?: IndicationType;
  geometry?: GeoJSON.Geometry;
  properties?: Partial<IndicationProperties>;
}

/**
 * PNG/PDF overlay
 */
export interface Overlay {
  id: string;
  name: string;
  url: string;
  bounds: [[number, number], [number, number], [number, number], [number, number]];
  opacity: number;
  visible: boolean;
  captureDate?: string;
  /** Raster tile fields (for full-resolution rendering of large images) */
  tilesetId?: string;
  tileStatus?: 'pending' | 'generating' | 'ready' | 'error';
  tileMinZoom?: number;
  tileMaxZoom?: number;
  /** R2 key for the original full-resolution source image (for a plan overlay:
   *  the source PDF, which stays authoritative for every re-bake) */
  sourceR2Key?: string;
  /** Plan-bake parameters — present only for PDF plan overlays. See PlanBake. */
  bake?: PlanBake;
  /** Companion DXF ingest state (rung 3) — see OverlayDxf. */
  dxf?: OverlayDxf;
  /** V4 entity version for optimistic concurrency */
  _version?: number;
}

/**
 * Companion-DXF state for a plan overlay (rung 3, R3-A).
 *
 * A DXF — client-native, or produced once per site by a desktop PDFIMPORT
 * conversion — is parsed server-side into per-layer GeoJSON + text entities
 * under `outPrefix` in R2 (served via the signed-assets pipeline). This record
 * is the STATE, not the geometry: what was ingested, whether it finished, the
 * manifest summary for the layer picker, the user's layer choices, and the
 * DXF→page registration fit. Persisted as overlays.dxf_json (0053), same
 * opaque-blob conventions as `bake`.
 */
export interface OverlayDxf {
  /** R2 key of the vector source: an uploaded .dxf
   *  (sites/<siteId>/vector/<assetId>.dxf) or, since R3-D, a .pdf — usually
   *  the plan's own sourceR2Key, vectorized server-side with no desktop
   *  conversion. */
  srcKey: string;
  /** For a .pdf srcKey: the 0-based page that was vectorized (the plan's
   *  bake.pageIndex when the source is the plan's own PDF). Re-read sends it
   *  again so the same page is parsed. Absent for a .dxf. */
  pageIndex?: number;
  /** Ingest job id — outputs live under sites/<siteId>/dxfingest/<jobId>/ */
  jobId: string;
  /** R2 prefix of the ingest outputs (manifest.json, layers/, texts.json) */
  outPrefix: string;
  status: 'ingesting' | 'ready' | 'error';
  error?: string;
  /** When the ingest was dispatched (ISO). The only honest progress signal the
   *  client has while the container works: the job reports nothing until it
   *  writes done.json, so elapsed time is what tells a user it is alive.
   *  Absent on jobs stamped before this field existed. */
  startedAt?: string;
  /** Set when the client stopped watching a job that never reported. NOT the
   *  same as "it failed": the container cannot say "still working", so this
   *  records our own giving up, and it is what offers the user a re-check
   *  instead of a re-upload. */
  stalled?: boolean;
  /** Manifest summary, stamped when the ingest lands (drives the layer picker
   *  without a manifest fetch on every project open). fillFeatures since the
   *  v3 ingest (HATCH/SOLID boundaries); absent on older ingests. */
  layers?: Array<{ index: number; name: string; lineFeatures: number; fillFeatures?: number; textCount: number; overflow?: number }>;
  /** Global DXF-space extents — the fallback registration when text matching
   *  cannot fit a transform. */
  extents?: { minX: number; minY: number; maxX: number; maxY: number };
  textTotal?: number;
  /** Layer indices the user picked for rendering (R3-B). */
  picked?: number[];
  /** Per-layer color overrides (layer index → hex). Absent = the drawing's
   *  own feature colors (v3+) or the palette. Exists because CAD colors are
   *  chosen for white paper — gray wiring on a gray ortho disappears. */
  colorOverrides?: Record<number, string>;
  /** Fill opacity for HATCH/SOLID polygons, 0-1. Absent = the 0.35 default. */
  fillOpacity?: number;
  /** How DXF coordinates reach the map (R3-B) — fitted once at ingest-ready by
   *  matching DXF text to layout labels; see utils/dxfRegistration.ts. */
  registration?: import('../utils/dxfRegistration').DxfRegistration;
  /** Manual whole-drawing correction, applied in PAGE space AFTER the fit.
   *  Deliberately separate from `registration` so Re-align, Re-read and the
   *  staleness healer can all re-fit underneath without destroying the
   *  user's correction — and so it can be reset in one click. Rotation is
   *  degrees about the drawing's own centre; scale is a multiplier there. */
  adjust?: { dx: number; dy: number; rotDeg: number; scale: number };
  /** Local as-built corrections (R3 micro adjust). Each zone is a PAGE-space
   *  quad plus a delta: everything whose centre falls inside moves with it.
   *
   *  Deliberately ZONES, not feature ids — a Re-read re-parses the DXF and
   *  can renumber every feature, which would silently invalidate id-based
   *  edits; a zone survives it. It also matches how deviation actually
   *  happens on site ("this row was built two metres over") and stays
   *  listable and individually resettable months later. */
  zones?: Array<import('../utils/dxfRegistration').DxfZone>;
}

/**
 * Plan-bake parameters for a PDF overlay.
 *
 * The browser can only rasterize a PDF plan into ONE Mapbox image texture
 * (~116-216 DPI on a large CAD sheet — mushy at module zoom). A "bake" renders
 * the page server-side at high DPI and serves it through the COG tile pipeline.
 *
 * These are PARAMETERS, not pixels: white removal (and, in later phases, crop /
 * erase) is recorded here and applied by the converter, so the source PDF stays
 * authoritative and every edit is reversible by re-baking. The browser applies
 * the same rules to the low-resolution proxy image for instant preview.
 */
export interface PlanBake {
  /** 0-based page of the source PDF */
  pageIndex: number;
  /** Page geometry in PDF points — drives the server-side DPI clamp */
  pageWidthPt: number;
  pageHeightPt: number;
  /** Requested render DPI (the server clamps it to what the page size allows) */
  dpi: number;
  /** 0 = keep the white sheet opaque; 1-255 = drop near-white to transparent */
  whiteThreshold: number;
  /**
   * Crop window, as fractions of the ORIGINAL PDF page (u0,v0 = top-left,
   * u1,v1 = bottom-right, all 0-1, v measured downwards like the rendered
   * raster). Absent = the whole page.
   *
   * Page-relative on purpose. The obvious alternative — storing it relative to
   * the overlay's current quad — breaks the moment you crop something already
   * cropped, because the quad has moved underneath. Composing into page space
   * at capture time keeps every recorded shape meaningful against the one
   * thing that never changes: the source PDF.
   */
  cropRect?: { u0: number; v0: number; u1: number; v1: number };
  /**
   * Erased areas, each a polygon of [u,v] points in the same page-fraction
   * space. Punched out of the alpha mask at bake time, so an erase survives
   * re-rendering — and can be undone later by dropping the polygon, because
   * the PDF is never modified.
   */
  erasePolygons?: Array<Array<[number, number]>>;
  /** What was actually baked. Compared against the live values to detect
   *  staleness — see getBakeState() in utils/planBake.ts. */
  lastBaked?: { hash: string; tilesetId: string };
  /** Set when a failed bake was rolled back to an earlier tileset whose
   *  placement is no longer known, so its tiles must not render — this keeps
   *  that state legible instead of letting the overlay look "never baked". */
  needsRebake?: boolean;
  /** The fingerprint that was live before the CURRENT attempt started. A
   *  re-bake stamps `lastBaked` at dispatch, which would otherwise destroy the
   *  provenance of the tiles still serving; keeping the previous one lets a
   *  failed attempt roll back to a plan that is still correctly placed instead
   *  of permanently demoting it to the proxy. */
  previousBaked?: { hash: string; tilesetId: string };
}

/**
 * Orthophoto layer
 * Matches original editor data structure with 4-corner coordinates.
 * GeoTIFF uploads use extracted bounds; manual images use map-center placement.
 */
export interface Orthophoto {
  id: string;
  name: string;
  url: string;
  /** 4-corner coordinates: [TL, TR, BR, BL] - used for Mapbox image source */
  coordinates: [[number, number], [number, number], [number, number], [number, number]];
  /** Bounding box {minLng, minLat, maxLng, maxLat} */
  bounds: { minLng: number; minLat: number; maxLng: number; maxLat: number };
  /** 0-100 percentage (matching original editor) */
  opacity: number;
  visible: boolean;
  /** true = manually placed JPG/PNG/GIF (supports repositioning) */
  isManual?: boolean;
  fileName?: string;
  fileSize?: number;
  width?: number;
  height?: number;
  captureDate?: string;
  /** Raster tile fields (for full-resolution rendering of large images) */
  tilesetId?: string;
  tileStatus?: 'pending' | 'generating' | 'ready' | 'error';
  tileMinZoom?: number;
  tileMaxZoom?: number;
  /** R2 key for the original full-resolution source image */
  sourceR2Key?: string;
  /** Whether background was removed (for tile generation) */
  bgRemoved?: boolean;
  /**
   * Stacking order. Lower = higher in the visual stack (rendered on top).
   * Mirrors the backend `sort_order` column, which the list endpoint returns
   * ASC — so `orthophotos[0]` (lowest sortOrder) is the topmost layer.
   */
  sortOrder?: number;
  /** V4 entity version for optimistic concurrency */
  _version?: number;
}

/**
 * Flight path
 */
export interface FlightPathStats {
  durationSec?: number;
  frameCount?: number;
  avgAltitude?: number;
}

export interface FlightPath {
  id: string;
  name: string;
  coordinates: [number, number, number?][];
  timestamps?: string[];
  visible: boolean;
  color: string;
  captureDate?: string;
  notes?: string;
  thermalVideoUrl?: string;
  visualVideoUrl?: string;
  /** Zoom-camera video (DJI `_Z`). Optional. */
  zoomVideoUrl?: string;
  /** Screen / liveview recording (DJI `_S`) — the app's current-view feed, NOT an HD camera feed. Optional. */
  screenVideoUrl?: string;
  /** Route synthesized from photo EXIF GPS because the flight had no SRT
   *  telemetry. Rendered in a distinct color; behaves like a normal flight path
   *  in every other respect. */
  isApproximate?: boolean;
  /** Google Drive file IDs of this flight's source files (SRTs + videos) —
   *  dedup keys so a re-import of the same Drive folder can skip the flight. */
  driveFileIds?: string[];
  flightStats?: FlightPathStats;
  /** Start GPS from SRT metadata */
  startGps?: string;
  /** End GPS from SRT metadata */
  endGps?: string;
  /** Start timestamp from SRT metadata */
  startTime?: string;
}

/**
 * Geo-referenced photo
 */
export interface GeoPhoto {
  id: string;
  url: string;
  thumbnailUrl?: string;
  coordinates: [number, number];
  bearing?: number;
  captureDate?: string;
  exifData?: Record<string, unknown>;
}

/**
 * Video strip
 */
export interface VideoStrip {
  id: string;
  name: string;
  coordinates: [number, number][];
  width: number;
  videoUrl?: string;
  visible: boolean;
}

/**
 * Project data structure
 */
export interface ProjectData {
  mapCenter: [number, number];
  mapZoom: number;
  mapBearing: number;
  mapPitch: number;
  indications: Indication[];
  overlays: Overlay[];
  orthophotos: Orthophoto[];
  flightPaths: FlightPath[];
  geoPhotos: GeoPhoto[];
  videoStrips: VideoStrip[];
  captureDates?: string[];
  visibleDates?: string[];
  // Monitoring data (persisted with project)
  monitoringConfig?: ProjectMonitoringConfig;
  monitoringSchema?: ParsedSchema;
  monitoringSampleData?: Record<string, Record<string, number | string | null>>;
  // SLD diagrams (persisted with project)
  sldDiagrams?: SLDDiagram[];
  // Tasks data (persisted with project)
  tasksData?: TasksConfig;
  // F-01A: Multi-layout array (new canonical shape). May be undefined or
  // empty for projects that have not imported a layout yet. Load-time
  // normalization in `loadProject` converts legacy `siteLayout` into a
  // length-1 array and hoists per-layout `topologyExceptions` into the
  // site-level map below.
  siteLayouts?: SiteLayout[];
  // F-01A: Site-level topology overrides (authoritative going forward).
  // Hoisted from any legacy per-layout `siteLayout.topologyExceptions` on
  // load. Last-wins on indication-id collisions during hoist.
  topologyExceptions?: Record<string, TopologyOverride>;
  /**
   * Vector PDF site layout — singleton compatibility alias.
   *
   * @deprecated F-01A — `siteLayouts[]` is the canonical shape. This field is
   * kept as a transitional alias to the first layout so existing singleton UI
   * (VectorLayoutTicket, ModuleGridPanel, LabelSchemaPanel, useLayoutOverlay,
   * etc.) continues to compile and render without change. All store actions
   * that mutate the layout update both `siteLayouts[0]` and this alias atomically.
   * Will be removed once the multi-layout UI lands in F-01B/F-01C.
   */
  siteLayout?: SiteLayout;
  // Layer visibility preferences (persisted with project)
  layerVisibility?: Partial<LayerVisibility>;
  layoutLabelLevels?: LayoutLabelLevelVisibility;
  indicationFilters?: Record<string, boolean>;
  indicationShowResolved?: boolean;
  dataPointAlarmFilters?: Record<string, boolean>;
  dataPointShowLabels?: boolean;
  // IV Results layer preferences (persisted with project)
  ivSessionVisibility?: Record<string, boolean>;
  ivDisplayMetric?: string;
  ivResultFilters?: Record<string, boolean>;
  /** Layer master switch, separate from the per-session selection. Absent
   *  in legacy blobs → derived as "any session selected" on load. */
  ivLayerVisible?: boolean;
}

/**
 * Project metadata
 */
export type SaveStatus = 'idle' | 'saved' | 'saving' | 'unsaved' | 'error' | 'conflict';

export interface ProjectMeta {
  id?: string;
  siteId?: string;
  siteName?: string;
  clientName?: string;
  isModified: boolean;
  lastSaved?: string;
  version?: number;
  saveStatus: SaveStatus;
}

interface ProjectState {
  // Metadata
  meta: ProjectMeta;

  // Data
  data: ProjectData;

  // Capture date filter
  selectedDate: string | null;
  availableDates: string[];
  visibleDates: string[];

  /**
   * F-01B: id of the currently active/selected layout for editor UI.
   *
   * Session-scoped (NOT part of `data` — not persisted to R2). Defaults to
   * `siteLayouts[0]?.id` after `loadProject` runs. Setting this updates the
   * deprecated `data.siteLayout` alias to point at the selected layout, so
   * existing singleton UI/panels continue to bind through the alias without
   * code changes.
   */
  activeLayoutId: string | null;

  // Actions
  setMeta: (meta: Partial<ProjectMeta>) => void;
  setData: (data: Partial<ProjectData>) => void;
  markModified: () => void;
  markSaved: () => void;
  setSaveStatus: (status: SaveStatus) => void;

  // Indications
  addIndication: (indication: Indication) => void;
  /**
   * Batch create (detection bulk-accept): ONE optimistic set() for the whole
   * batch, then chunked create-only POSTs (≤200/req) to /indications/bulk.
   * The endpoint returns counts + conflict-id arrays only (no created list),
   * so every sent id NOT present in conflicts/staleConflicts is stamped
   * `_version: 1` locally — without the stamp every later edit of these
   * indications skips the entity PATCH and is SILENTLY LOST on reload.
   * Conflicting/failed ids are rolled back out of the local store.
   * Local-only projects (no siteId) resolve with all ids created.
   */
  addIndicationsBulk: (indications: Indication[]) => Promise<{
    createdIds: string[];
    conflictIds: string[];
    failedIds: string[];
  }>;
  updateIndication: (id: string, updates: IndicationUpdate) => void;
  /** LOCAL-ONLY bulk apply of server-confirmed batch-apply results (P2) —
   *  merges geometry/properties and stamps the server-issued _version without
   *  firing any entity writes. Callers own the server round-trip. */
  applyServerIndicationRows: (
    rows: Array<{ id: string; geometry?: GeoJSON.Geometry; properties?: Partial<IndicationProperties>; _version: number }>,
  ) => void;
  /** LOCAL-ONLY wholesale replace after an authoritative refetch (post-undo). */
  replaceIndications: (indications: Indication[]) => void;
  /**
   * Transition resolution status (Phase 1) via the server-stamped endpoint.
   * Resolves 'updated' (persisted), 'local' (blob site, optimistic only),
   * 'conflict' (409 — server state reloaded), 'error' (rolled back), or
   * 'noop' (viewer / unknown id). Safe to fire-and-forget.
   */
  setIndicationStatus: (
    id: string,
    status: IndicationStatus,
    note?: string
  ) => Promise<'updated' | 'local' | 'conflict' | 'error' | 'noop'>;
  /**
   * FL-1: field-link notes edit (properties.description ONLY). No-ops
   * unless the active session is a field-kind share link — authenticated
   * operators keep the normal updateIndication path.
   */
  setIndicationFieldNotes: (
    id: string,
    description: string
  ) => Promise<'updated' | 'conflict' | 'error' | 'noop'>;
  /** FL-2.5: field-link geometry edit — own-created indications only
   *  (createdVia.linkId must match; the backend re-enforces). Optional
   *  topologyUpdates (from computeTopologyUpdates on the new geometry) ride
   *  the same PATCH so station/inverter/… follow the moved mark. */
  setIndicationFieldGeometry: (
    id: string,
    geometry: GeoJSON.Geometry,
    topologyUpdates?: Record<string, unknown>
  ) => Promise<'updated' | 'conflict' | 'error' | 'noop'>;
  /** FL-2.5: field-link delete — own-created indications only. */
  removeIndicationAsField: (
    id: string
  ) => Promise<'deleted' | 'conflict' | 'error' | 'noop'>;
  /** FL-3a: attach a field photo (already-downscaled blob). Field links
   *  only; the server appends to properties.fieldPhotos + ops-logs.
   *  'limit' = the 12-photo cap on this indication was reached. */
  addIndicationFieldPhoto: (
    id: string,
    photo: Blob,
    fileName: string
  ) => Promise<'updated' | 'conflict' | 'limit' | 'error' | 'noop'>;
  /** FL-3a: remove one OWN field photo (entry.linkId must match). */
  removeIndicationFieldPhoto: (
    id: string,
    photoId: string
  ) => Promise<'updated' | 'conflict' | 'error' | 'noop'>;
  removeIndication: (id: string) => void;
  /** LOCAL-ONLY batch removal (one store write, no entity-API calls, no R2
   *  asset scheduling) — the batch_delete OPERATION owns the server deletes
   *  and its undo re-inserts rows, so image assets must survive deletion
   *  (orphaned assets of never-undone deletes are the retention cron's
   *  concern, not this path's). */
  removeIndicationsLocal: (ids: string[]) => void;
  getIndicationsByType: (type: IndicationType) => Indication[];
  getIndicationsByDate: (date: string) => Indication[];

  // Overlays
  addOverlay: (overlay: Overlay) => void;
  updateOverlay: (id: string, updates: Partial<Overlay>) => void;
  removeOverlay: (id: string) => void;
  setOverlayOpacity: (id: string, opacity: number) => void;
  toggleOverlayVisibility: (id: string) => void;

  // Orthophotos
  addOrthophoto: (orthophoto: Orthophoto) => void;
  updateOrthophoto: (id: string, updates: Partial<Orthophoto>) => void;
  removeOrthophoto: (id: string) => void;
  setOrthophotoOpacity: (id: string, opacity: number) => void;
  toggleOrthophotoVisibility: (id: string) => void;
  /** Move an orthophoto one step up (toward top, index 0) or down in the stack. */
  moveOrthophoto: (id: string, direction: 'up' | 'down') => void;

  // Flight paths
  addFlightPath: (flightPath: FlightPath) => void;
  updateFlightPath: (id: string, updates: Partial<FlightPath>) => void;
  removeFlightPath: (id: string) => void;
  toggleFlightPathVisibility: (id: string) => void;
  clearFlightPaths: () => void;

  // Geo photos
  addGeoPhoto: (photo: GeoPhoto) => void;
  removeGeoPhoto: (id: string) => void;
  updateGeoPhotoCoords: (id: string, coordinates: [number, number]) => void;

  // Site layout
  setSiteLayout: (layout: SiteLayout | undefined) => void;
  /** Re-point the active layout at the plan overlay it registers (WP-A). */
  setLayoutPlanOverlay: (overlayId: string | undefined) => void;
  /** F-01B: append a new layout to siteLayouts[] and activate it. Does NOT
   *  delete any previously-imported layout's assets. */
  addSiteLayoutAppend: (layout: SiteLayout) => void;
  /** F-01B: remove a layout by id. Schedules deletion of its PDF + page image
   *  assets. If the active layout was removed, picks the next layout in array
   *  order as the new active (else the previous, else clears active). */
  removeSiteLayoutById: (layoutId: string) => void;
  /** F-01B: select which layout is active for editor UI. Updates the
   *  deprecated `data.siteLayout` alias atomically so existing singleton
   *  panels rebind without code changes. Passing `null` clears the alias. */
  setActiveLayoutId: (layoutId: string | null) => void;
  updateSiteLayoutAnchors: (anchors: AnchorPair[], transform?: LayoutTransform) => void;
  /** Replace the active layout's textEntities. Pass `reclassified: true` from
   *  flows that re-ran classification (schema/pattern apply) so the layout's
   *  classifierVersion stamp stays truthful; position/text repairs omit it. */
  updateSiteLayoutEntities: (entities: LayoutTextEntity[], opts?: { reclassified?: boolean }) => void;
  updateSiteLayoutDisplay: (updates: { labelOpacity?: number; labelVisible?: boolean; imageOpacity?: number; imageVisible?: boolean }) => void;
  updateSiteLayoutException: (indicationId: string, override: TopologyOverride) => void;
  updateSiteLayoutConfig: (config: ClassificationPattern[]) => void;
  /** Persist dismissed family signatures on the active layout. */
  setIgnoredFamilies: (signatures: string[]) => void;
  /** Copy the ACTIVE layout's labelSchemas + classificationConfig to every
   *  layout of the site and re-classify each one's entities with it.
   *  Multi-sheet sites from one drawing office share a label grammar —
   *  this removes the per-layout setup repetition (e.g. Zmorot ×4). */
  applyClassificationToAllLayouts: () => void;
  setLabelSchemas: (schemas: import('../domain/layout/types').LabelSchema[]) => void;
  addTextEntity: (entity: LayoutTextEntity) => void;
  removeTextEntity: (entityId: string) => void;
  updateTextEntity: (entityId: string, updates: Partial<LayoutTextEntity>) => void;
  batchUpdateTextEntities: (updates: Array<{ id: string; cx: number; cy: number; manuallyMoved?: boolean }>) => void;
  /** Delete many labels in ONE store write (multi-select Delete All — OCR
   *  cleanup deletes hundreds; per-entity removes would be N undo steps). */
  removeTextEntities: (entityIds: string[]) => void;
  /** Set a manual level override on many labels in ONE store write
   *  (multi-select batch typology change). Overrides survive re-classify. */
  batchSetLabelLevel: (entityIds: string[], level: string) => void;

  // Module grid
  setModuleGridConfig: (config: ModuleGridConfig | undefined) => void;
  setStringOverride: (entityId: string, override: StringOverride | null) => void;
  /** Merge the same override PATCH into many strings in ONE store write
   *  (multi-select batch grid editing — owner request 2026-09-02). */
  batchPatchStringOverrides: (entityIds: string[], patch: Partial<StringOverride>) => void;
  /** Delete many strings' overrides in ONE store write (batch Reset). */
  batchRemoveStringOverrides: (entityIds: string[]) => void;
  /** Apply DIFFERENT patches per string in ONE store write (whole-grid drag:
   *  the same map delta projects to different along/perp offsets per string
   *  direction). */
  batchApplyStringOverridePatches: (patches: Record<string, Partial<StringOverride>>) => void;
  removeStringOverride: (entityId: string) => void;

  // Date filter
  setSelectedDate: (date: string | null) => void;
  updateAvailableDates: () => void;
  addCaptureDate: (date: string) => void;
  removeCaptureDate: (date: string) => void;
  toggleDateVisibility: (date: string) => void;
  setAllDatesVisible: () => void;
  setNoDatesVisible: () => void;

  // Project operations
  loadProject: (data: ProjectData, meta: Partial<ProjectMeta>) => void;
  loadProjectFromServer: (siteId: string, siteName: string, clientName?: string) => Promise<ProjectData>;
  clearProject: () => void;
  exportProject: () => ProjectData;

  // Entity-write coordination — wait for in-flight create/update PATCHes to settle
  // (so callers like syncProjectToServer see fresh _version values).
  flushPendingEntityWrites: () => Promise<void>;
}

const defaultData: ProjectData = {
  mapCenter: [34.8, 31.5], // Default to Israel
  mapZoom: 15,
  mapBearing: 0,
  mapPitch: 0,
  indications: [],
  overlays: [],
  orthophotos: [],
  flightPaths: [],
  geoPhotos: [],
  videoStrips: [],
  captureDates: [],
  visibleDates: [],
};

const defaultMeta: ProjectMeta = {
  isModified: false,
  saveStatus: 'idle',
};

// ─── F-01A: Multi-layout compatibility normalization ──────────────────────
//
// Converts legacy persisted shapes into the new canonical shape and keeps
// the deprecated singleton `siteLayout` alias in sync with `siteLayouts[0]`.
//
// Behavior:
//   1. If `data.siteLayout` exists and `data.siteLayouts` is missing/empty,
//      promote the legacy singleton into a length-1 array (preserving id/name
//      and every other field, with `topologyExceptions` stripped — see step 2).
//   2. For every layout in the resulting array, hoist any per-layout
//      `topologyExceptions` into the site-level `data.topologyExceptions`
//      map (last-wins on indication-id collisions, deterministic by array
//      iteration order) and strip the per-layout key.
//   3. Refresh `data.siteLayout` as the alias to the first layout (or
//      `undefined` if the array is empty).
//
// Idempotent: running normalization twice on the same data is a no-op.
//
// PURE — does not call into Zustand. Used by `loadProject` and by tests.
type LayoutWithLegacyExceptions = SiteLayout & {
  topologyExceptions?: Record<string, TopologyOverride>;
};
type ProjectDataWithLegacy = ProjectData & {
  siteLayout?: LayoutWithLegacyExceptions;
  siteLayouts?: LayoutWithLegacyExceptions[];
};

export function normalizeProjectLayouts(data: ProjectData): ProjectData {
  const legacy = data as ProjectDataWithLegacy;

  // Step 1: resolve the canonical source array, falling back to the legacy
  // singleton (treated as length-1) only when no array is present. Track
  // whether the fallback was used so the idempotency check can detect that
  // a normalization step actually happened on this pass.
  let source: LayoutWithLegacyExceptions[] | undefined = legacy.siteLayouts;
  const sourceWasFromAlias = (!source || source.length === 0) && !!legacy.siteLayout;
  if (sourceWasFromAlias) {
    source = [legacy.siteLayout as LayoutWithLegacyExceptions];
  }

  // Step 2: detect whether any per-layout topologyExceptions need hoisting.
  // We compute this before allocating any new arrays so the no-op case stays
  // reference-stable for downstream Zustand subscribers.
  const hoisted: Record<string, TopologyOverride> = {};
  let needsHoist = false;
  if (source) {
    for (const l of source) {
      if (l?.topologyExceptions && Object.keys(l.topologyExceptions).length > 0) {
        needsHoist = true;
        for (const [k, v] of Object.entries(l.topologyExceptions)) {
          hoisted[k] = v;
        }
      }
    }
  }

  // Step 3: build the cleaned array. Reuse the input reference verbatim when
  // no hoisting is needed; only allocate a new array when at least one layout
  // carries per-layout exceptions that must be stripped.
  let cleaned: SiteLayout[] | undefined;
  if (!source) {
    cleaned = undefined;
  } else if (!needsHoist) {
    cleaned = source as SiteLayout[];
  } else {
    cleaned = source.map((l) => {
      if (!l) return l as unknown as SiteLayout;
      const withEx = l as LayoutWithLegacyExceptions;
      if (withEx.topologyExceptions && Object.keys(withEx.topologyExceptions).length > 0) {
        const { topologyExceptions: _drop, ...rest } = withEx;
        void _drop;
        return rest as SiteLayout;
      }
      return l as SiteLayout;
    });
  }

  // Step 4: merge site-level exceptions. Any pre-existing entries at the site
  // level win over a per-layout entry for the same indicationId (treats
  // already-hoisted data as authoritative).
  const mergedExceptions: Record<string, TopologyOverride> = {
    ...hoisted,
    ...(legacy.topologyExceptions ?? {}),
  };
  const hasExceptions = Object.keys(mergedExceptions).length > 0;
  const alias = cleaned && cleaned.length > 0 ? cleaned[0] : undefined;

  // Step 5: idempotency short-circuit. If nothing actually needed to change,
  // return the original input verbatim so reference equality holds for
  // downstream subscribers.
  const arrayUnchanged = !sourceWasFromAlias && cleaned === legacy.siteLayouts;
  const aliasUnchanged = alias === legacy.siteLayout;
  let exceptionsUnchanged = false;
  if (!hasExceptions && legacy.topologyExceptions === undefined) {
    exceptionsUnchanged = true;
  } else if (
    hasExceptions &&
    !needsHoist &&
    legacy.topologyExceptions !== undefined
  ) {
    // We didn't hoist anything this pass, so the merged map is just a clone
    // of `legacy.topologyExceptions`. Confirm key parity before declaring it
    // unchanged.
    const existing = legacy.topologyExceptions;
    const a = Object.keys(mergedExceptions);
    const b = Object.keys(existing);
    if (
      a.length === b.length &&
      a.every((k) => Object.prototype.hasOwnProperty.call(existing, k))
    ) {
      exceptionsUnchanged = true;
    }
  }

  if (arrayUnchanged && aliasUnchanged && exceptionsUnchanged) {
    return data;
  }

  return {
    ...data,
    siteLayouts: cleaned,
    siteLayout: alias,
    topologyExceptions: hasExceptions
      ? (exceptionsUnchanged && legacy.topologyExceptions
          ? legacy.topologyExceptions
          : mergedExceptions)
      : legacy.topologyExceptions,
  };
}

// ─── F-01B: Active-layout action helper ────────────────────────────────────
//
// Every existing singleton store action that mutates "the layout" routes
// through this helper. It now targets the **active** layout (the one whose
// id matches `state.activeLayoutId`), with deterministic fallbacks:
//
//   1. If `activeLayoutId` matches a layout in `siteLayouts[]` → that one.
//   2. Else if `siteLayouts[]` is non-empty → `siteLayouts[0]`.
//   3. Else if the legacy `data.siteLayout` singleton is present → that.
//   4. Else no-op (caller should early-return).
//
// After the update, both the array entry AND the deprecated `data.siteLayout`
// alias are rewritten to point at the new layout. The alias always equals
// whichever layout the active id resolves to, so all existing singleton UI
// (panels, popups, anchor mode) automatically follows the active selection
// without component-level code changes.
//
// This replaces F-01A's `applyFirstLayoutUpdate` — the F-01A name implied
// the head was always the target, which is no longer true under F-01B.
function applyActiveLayoutUpdate(
  state: ProjectState,
  updater: (layout: SiteLayout) => SiteLayout,
): { data: ProjectData; meta: ProjectMeta } | null {
  const layouts = state.data.siteLayouts;
  if (!layouts || layouts.length === 0) {
    // Pre-normalize edge case: only the legacy singleton is set.
    const legacy = state.data.siteLayout;
    if (!legacy) return null;
    const updated = updater(legacy);
    return {
      data: { ...state.data, siteLayouts: [updated], siteLayout: updated },
      meta: { ...state.meta, isModified: true },
    };
  }
  const activeId = state.activeLayoutId;
  const idx = activeId ? layouts.findIndex((l) => l.id === activeId) : -1;
  const targetIdx = idx >= 0 ? idx : 0;
  const target = layouts[targetIdx];
  const updated = updater(target);
  const newLayouts = layouts.slice();
  newLayouts[targetIdx] = updated;
  return {
    data: {
      ...state.data,
      siteLayouts: newLayouts,
      siteLayout: updated,
    },
    meta: { ...state.meta, isModified: true },
  };
}

// ─── V4 P2.5: In-Flight Entity Write Guard ─────────────────────────────────
//
// Tracks indication IDs with unconfirmed entity writes.
// Value = pre-mutation snapshot (null for pending adds = "didn't exist").
// exportProject() uses this to serialize only confirmed state, preventing
// blob autosave from persisting unconfirmed optimistic mutations.
//
// Lifecycle per mutation:
//   1. Before entity API call: set(id, snapshot)
//   2. On entity success: delete(id)
//   3. On entity failure + rollback: delete(id)
//
const pendingEntityWrites = new Map<string, Indication | null>();

/**
 * Return indications with only confirmed state — pending entity writes
 * are replaced with their pre-mutation snapshots (or excluded for adds).
 * Called by exportProject() to guard blob autosave serialization.
 */
function getConfirmedIndications(indications: Indication[]): Indication[] {
  if (pendingEntityWrites.size === 0) return indications;

  const result: Indication[] = [];

  for (const ind of indications) {
    if (!pendingEntityWrites.has(ind.id)) {
      // Not pending — confirmed, use as-is
      result.push(ind);
    } else {
      const snapshot = pendingEntityWrites.get(ind.id) ?? null;
      if (snapshot === null) {
        // Pending add — not yet confirmed to exist, exclude
        continue;
      }
      // Pending update — use pre-mutation snapshot
      result.push(snapshot);
    }
  }

  // Re-include optimistically removed indications (pending delete, unconfirmed)
  for (const [, snapshot] of pendingEntityWrites) {
    if (snapshot !== null && snapshot !== undefined && !result.some((ind) => ind.id === snapshot.id)) {
      result.push(snapshot);
    }
  }

  return result;
}

// ─── V4 P2.7: In-Flight Entity Write Guards for Overlays + Orthophotos ────
//
// Same pattern as pendingEntityWrites for indications.
// Tracks IDs with unconfirmed entity writes.
// Value = pre-mutation snapshot (null for pending adds = "didn't exist").
// getConfirmedOverlays/getConfirmedOrthophotos() guard exportProject()
// so blob autosave never serializes unconfirmed optimistic mutations.
//
const pendingOverlayWrites = new Map<string, Overlay | null>();
const pendingOrthophotoWrites = new Map<string, Orthophoto | null>();

// ─── F-02E-2B: share-viewer write suppression ──────────────────────────────
//
// Share visitors load the project via the read-only `/share/:token/viewer`
// shell. The backend (F-02C-2/3) rejects any PATCH that touches an entity
// row, so when the viewer interacts with the legend's visibility checkbox or
// opacity slider, the optimistic local flip fires immediately — then the
// auto-persist PATCH lands with 401 SHARE_SESSION_EXPIRED and the
// flushOverlay*/flushOrthophoto* catch rolls local state back to pre-flip.
// Net effect for the viewer: the checkbox flickers but never sticks, the
// slider snaps back to its starting value, and the console fills with 401s.
//
// `isShareViewerActive()` mirrors the same invariant used by the network
// layer (`apiFetch.useShareMode`) and the UI hook (`useIsReadOnlyViewer`):
// a share session is active AND there is no Bearer token. We import the
// stores' bare `useStore` references and call `getState()` synchronously
// inside the four mutating actions; no React subscription is created.
//
// When the predicate is true the actions skip the persist scheduling and
// (for orthophoto) the `meta.isModified` flag, so the legend behaves as a
// pure local UI control for the visitor. Authenticated owners are entirely
// unaffected because `authStore.token` flips the predicate off.
function isShareViewerActive(): boolean {
  return useShareSessionStore.getState().active && !useAuthStore.getState().token;
}

/**
 * FL-1: is the active share session a FIELD-kind link? Field visitors are
 * still share viewers everywhere (all F-02E-2B guards keep holding) EXCEPT
 * the two narrow write actions that consult this predicate directly
 * (status transition + notes edit). Server-side enforcement is
 * authoritative; this only decides whether the client attempts the call.
 */
function isFieldLinkActive(): boolean {
  return (
    isShareViewerActive() &&
    useShareSessionStore.getState().view?.kind === 'field'
  );
}

/**
 * FL-3a Flow C: resolve once the indication `id` is SERVER-confirmed (has a
 * `_version` — addIndication's create POST adopts the server row async), or
 * with null on timeout / rollback (create failed → row removed). Used to
 * safely fire a photo attach right after a field quick-add create.
 */
export function waitForIndicationConfirmed(
  id: string,
  timeoutMs = 10_000,
): Promise<Indication | null> {
  const lookup = (): Indication | undefined =>
    useProjectStore.getState().data.indications.find((i) => i.id === id);
  const current = lookup();
  if (current?._version) return Promise.resolve(current);
  if (!current) return Promise.resolve(null);

  return new Promise((resolve) => {
    let unsub: () => void = () => {};
    const timer = window.setTimeout(() => {
      unsub();
      resolve(null);
    }, timeoutMs);
    unsub = useProjectStore.subscribe((state) => {
      const ind = state.data.indications.find((i) => i.id === id);
      if (ind?._version) {
        window.clearTimeout(timer);
        unsub();
        resolve(ind);
      } else if (!ind) {
        // Optimistic add rolled back — the create failed.
        window.clearTimeout(timer);
        unsub();
        resolve(null);
      }
    });
  });
}

/**
 * F-02E-2B/2C share-section gate.
 *
 * Mirrors the backend `shareReadable` surface: a share visitor sees data
 * for an optional section ONLY when the owner opted-in by listing the
 * section name in `exposed_sections`. For authenticated callers and
 * non-share contexts the answer is always true — owner editor behaviour
 * is unchanged.
 *
 * Used by `loadProject` / `loadProjectFromServer` to decide whether to
 * hydrate optional-section stores (monitoring config, IV session list,
 * tasks/issues entity fetch). When the answer is false we either skip
 * the call entirely or clear the related store, so the share viewer
 * never fires endpoints whose section the owner didn't expose.
 *
 * Generic over the four optional sections that gate their own data
 * fetches at project-load time. The implicit `'core'` section is
 * always allowed and isn't covered here — core data (project blob,
 * indications, overlays, orthophotos) loads unconditionally.
 */
function isShareSectionAllowed(
  section: 'monitoring' | 'iv' | 'tasks' | 'scores' | 'state',
): boolean {
  if (!isShareViewerActive()) return true;
  const view = useShareSessionStore.getState().view;
  return !!view?.exposed_sections?.includes(section);
}

/**
 * Back-compat alias used by the F-02E-2B v3 monitoring branches of
 * `loadProject`. Kept as a thin wrapper so the call sites read clearly;
 * delegates to the new generic helper.
 */
function isMonitoringAllowedForCurrentSession(): boolean {
  return isShareSectionAllowed('monitoring');
}

// ─── Opacity slider persist debounce ──────────────────────────────────────
//
// Slider drags fire onChange per pixel; each call used to PATCH the entity
// row immediately. With ~30 ticks/drag and ~80 ms PATCH RTT, all but the
// first PATCH carry the same stale _version and 409 — the conflict handler
// then reloads + re-renders + spawns more 409s. Solution: keep local state
// updates immediate (visual feedback intact), but trailing-edge debounce the
// PATCH with in-flight serialization. Each ID has at most one PATCH in
// flight at a time; values that arrive during flight replace the pending
// value and fire after the current PATCH settles.
//
const OPACITY_DEBOUNCE_MS = 250;
const overlayOpacityPending = new Map<string, number>();
const overlayOpacityTimer = new Map<string, ReturnType<typeof setTimeout>>();
const overlayOpacityInFlight = new Set<string>();
const orthoOpacityPending = new Map<string, number>();
const orthoOpacityTimer = new Map<string, ReturnType<typeof setTimeout>>();
const orthoOpacityInFlight = new Set<string>();

// ─── Visibility-toggle in-flight serialization (P-31) ──────────────────────
//
// Rapid uncheck/recheck of an overlay or orthophoto used to fire back-to-back
// PATCHes that both bound the same stale _version, 409ing the second and
// triggering a server-truth reload that visually flickered the layer. Mirrors
// the opacity in-flight pattern above: optimistic local flips stay synchronous
// for instant visual feedback; PATCH issuance is serialized per id; rapid
// clicks during flight record only the *latest* intended visibility and fire
// a single follow-up PATCH from the in-flight handler's finally block using
// the fresh _version. No debounce — visibility is a discrete click, not a
// continuous drag.
//
const overlayVisibilityInFlight = new Set<string>();
const overlayVisibilityPending = new Map<string, boolean>();
const orthoVisibilityInFlight = new Set<string>();
const orthoVisibilityPending = new Map<string, boolean>();

// ─── Pending Entity Promises (flush gate) ──────────────────────────────────
//
// Tracks the *outer* promise of every in-flight entity write — the chain that
// includes the success handler that writes _version back into the store.
// `flushPendingEntityWrites()` lets callers (notably syncProjectToServer) wait
// until every in-flight create/update has settled and the store has the fresh
// _version values, so subsequent PATCHes don't 412 on stale versions or
// silently no-op on missing versions.
//
const pendingEntityPromises = new Set<Promise<unknown>>();

function trackEntityPromise<T>(p: Promise<T>): Promise<T> {
  pendingEntityPromises.add(p);
  // `.finally()` returns a derived promise that RE-REJECTS when `p` fails; if
  // left uncaught it surfaces as an unhandled rejection on every failed entity
  // write (app-wide, and it fails the test suite's unhandled-error check). The
  // caller awaits and handles `p` itself — this branch only needs cleanup, so
  // swallow its rejection.
  p.finally(() => pendingEntityPromises.delete(p)).catch(() => { /* caller handles p */ });
  return p;
}

// Overlay entity-write helpers. Get/set are Zustand store accessors threaded
// through from the setter; typed as `any` to avoid importing the full
// ProjectStore type into module scope (it's defined later in the file, and
// would force a forward-declaration dance for no real benefit). The same
// applies to the opacity/visibility flush helpers further down.

/** Copy just the keys of `shape` out of `src`. Used to touch only the fields a
 *  write was carrying, never the whole overlay. */
function pickOverlayKeys(
  src: Partial<Overlay> | null | undefined,
  shape: Partial<Overlay>,
): Partial<Overlay> {
  const out: Record<string, unknown> = {};
  if (!src) return out as Partial<Overlay>;
  for (const key of Object.keys(shape)) out[key] = (src as Record<string, unknown>)[key];
  return out as Partial<Overlay>;
}

/**
 * Resolve a 409 on an overlay write WITHOUT regressing local state.
 *
 * Every overlay write used to answer a conflict by re-reading the row and
 * replacing the whole local overlay with it. That read is a second round trip,
 * so anything committed in between — including the user's own later edits —
 * was wiped from the store while the server kept it, leaving the browser older
 * than the database. Observed live: an edge-by-edge drag issued 11 writes in
 * 35 s and ended with the store ~70 m from the saved position.
 *
 * A conflict here is almost always this tab racing itself, and the local value
 * is the newer intent — so keep it, adopt the server's version, and re-issue
 * the SAME fields once. CAS does the rest: if something else committed in the
 * gap, the retry conflicts again and we stop.
 *
 * If the write genuinely cannot be saved, revert the attempted fields to what
 * IS stored. Keeping them would leave the screen showing an edit that silently
 * never persisted — divergence the user only discovers on reload.
 */
async function resolveOverlayWriteConflict(
  siteId: string,
  id: string,
  updates: Partial<Overlay>,
  fallback: Overlay | null | undefined,
  label: string,
  get: () => any,
  set: (updater: any) => void,
): Promise<void> {
  const applyFields = (fields: Partial<Overlay>) =>
    set((state: any) => ({
      data: {
        ...state.data,
        overlays: state.data.overlays.map((o: Overlay) =>
          o.id === id ? { ...o, ...fields } : o,
        ),
      },
    }));

  const serverOv = await fetchSingleEntityOverlay(siteId, id);
  if (!serverOv?._version) {
    // fetchSingleEntityOverlay swallows its errors and returns null, so this
    // covers "row is gone" and "the read failed" alike; neither leaves us able
    // to save, so show what we last knew to be stored.
    console.warn(`[V4 CONFLICT] ${label}: no current version for ${id}; reverting the unsaved change`);
    applyFields(pickOverlayKeys(fallback, updates));
    return;
  }

  applyFields({ _version: serverOv._version } as Partial<Overlay>);
  // Retry the CURRENT value of those fields, not the one captured when this
  // write was issued. In a drag burst two writes bind the same version; if the
  // LATER one commits and the EARLIER one conflicts, replaying the earlier
  // payload would push the older position back over the newer — reintroducing
  // the very divergence this helper exists to prevent, just in the opposite
  // direction. Re-reading makes the retry carry the latest intent, so the two
  // stores converge on the same value whichever write lost.
  const live = get().data.overlays.find((o: Overlay) => o.id === id);
  if (!live) return; // deleted while we were recovering — nothing to write
  const payload = pickOverlayKeys(live, updates);
  try {
    const retried = await updateEntityOverlay(siteId, id, payload, serverOv._version);
    applyFields({ _version: retried._version } as Partial<Overlay>);
  } catch (retryErr) {
    console.warn(`[V4 CONFLICT] ${label}: retry failed for ${id}; showing the stored values`, retryErr);
    applyFields(pickOverlayKeys(serverOv, updates));
  }
}

async function flushOverlayOpacity(
  id: string,
  get: () => any,
  set: (updater: any) => void,
): Promise<void> {
  if (overlayOpacityInFlight.has(id)) return; // a PATCH is in flight; finally-block will retrigger us

  const opacity = overlayOpacityPending.get(id);
  if (opacity === undefined) return;
  overlayOpacityPending.delete(id);

  const overlay = get().data.overlays.find((o: Overlay) => o.id === id);
  const siteId = get().meta.siteId;
  const version = overlay?._version;
  if (!overlay || !siteId || !version) return;

  if (!pendingOverlayWrites.has(id)) pendingOverlayWrites.set(id, overlay);
  overlayOpacityInFlight.add(id);

  try {
    const updated = await trackEntityPromise(
      updateEntityOverlay(siteId, id, { opacity }, version)
    );
    pendingOverlayWrites.delete(id);
    set((state: any) => ({
      data: {
        ...state.data,
        overlays: state.data.overlays.map((o: Overlay) =>
          o.id === id ? { ...o, _version: updated._version } : o
        ),
      },
    }));
  } catch (err) {
    if (isSpatialConflictError(err)) {
      // Keep the user's opacity; adopt the version and retry once. Replacing
      // the whole overlay here used to revert an in-flight drag's bounds.
      await resolveOverlayWriteConflict(
        siteId, id, { opacity }, pendingOverlayWrites.get(id), 'Overlay opacity', get, set,
      );
    } else {
      console.warn('[V4] Overlay opacity persist failed, rolling back:', err);
      const snapshot = pendingOverlayWrites.get(id);
      if (snapshot) {
        const snapOpacity = snapshot.opacity;
        set((state: any) => ({
          data: {
            ...state.data,
            overlays: state.data.overlays.map((o: Overlay) =>
              o.id === id ? { ...o, opacity: snapOpacity } : o
            ),
          },
        }));
      }
    }
    pendingOverlayWrites.delete(id);
  } finally {
    overlayOpacityInFlight.delete(id);
    // If a newer value arrived while we were in flight, fire a follow-up.
    if (overlayOpacityPending.has(id)) {
      void flushOverlayOpacity(id, get, set);
    }
  }
}

async function flushOrthophotoOpacity(
  id: string,
  get: () => any,
  set: (updater: any) => void,
): Promise<void> {
  if (orthoOpacityInFlight.has(id)) return;

  const opacity = orthoOpacityPending.get(id);
  if (opacity === undefined) return;
  orthoOpacityPending.delete(id);

  const ortho = get().data.orthophotos.find((o: Orthophoto) => o.id === id);
  const siteId = get().meta.siteId;
  const version = ortho?._version;
  if (!ortho || !siteId || !version) return;

  if (!pendingOrthophotoWrites.has(id)) pendingOrthophotoWrites.set(id, ortho);
  orthoOpacityInFlight.add(id);

  try {
    const updated = await trackEntityPromise(
      updateEntityOrthophoto(siteId, id, { opacity }, version)
    );
    pendingOrthophotoWrites.delete(id);
    set((state: any) => ({
      data: {
        ...state.data,
        orthophotos: state.data.orthophotos.map((o: Orthophoto) =>
          o.id === id ? { ...o, _version: updated._version } : o
        ),
      },
    }));
  } catch (err) {
    if (isSpatialConflictError(err)) {
      console.warn('[V4 CONFLICT] Orthophoto opacity conflict, reloading:', id);
      const serverOp = await fetchSingleEntityOrthophoto(siteId, id);
      if (serverOp) {
        set((state: any) => ({
          data: {
            ...state.data,
            orthophotos: state.data.orthophotos.map((o: Orthophoto) => (o.id === id ? serverOp : o)),
          },
        }));
      }
    } else {
      console.warn('[V4] Orthophoto opacity persist failed, rolling back:', err);
      const snapshot = pendingOrthophotoWrites.get(id);
      if (snapshot) {
        const snapOpacity = snapshot.opacity;
        set((state: any) => ({
          data: {
            ...state.data,
            orthophotos: state.data.orthophotos.map((o: Orthophoto) =>
              o.id === id ? { ...o, opacity: snapOpacity } : o
            ),
          },
        }));
      }
    }
    pendingOrthophotoWrites.delete(id);
  } finally {
    orthoOpacityInFlight.delete(id);
    if (orthoOpacityPending.has(id)) {
      void flushOrthophotoOpacity(id, get, set);
    }
  }
}

// P-31: in-flight serialization for overlay/orthophoto visibility toggles.
// Public toggle setters do the optimistic local flip and either fire the
// PATCH here (when no PATCH is in flight) or record the latest intent in
// overlayVisibilityPending. The finally block re-fires using the fresh
// _version that the .then has just written into the store.
async function flushOverlayVisibility(
  id: string,
  newVisible: boolean,
  get: () => any,
  set: (updater: any) => void,
): Promise<void> {
  const siteId = get().meta.siteId;
  const overlay = get().data.overlays.find((o: Overlay) => o.id === id);
  const version = overlay?._version;
  if (!siteId || !version) return;

  if (overlay && !pendingOverlayWrites.has(id)) pendingOverlayWrites.set(id, overlay);
  overlayVisibilityInFlight.add(id);

  try {
    const updated = await updateEntityOverlay(siteId, id, { visible: newVisible }, version);
    pendingOverlayWrites.delete(id);
    set((state: any) => ({
      data: {
        ...state.data,
        overlays: state.data.overlays.map((o: Overlay) =>
          o.id === id ? { ...o, _version: updated._version } : o
        ),
      },
    }));
  } catch (err) {
    if (isSpatialConflictError(err)) {
      // Same reasoning as opacity: ticking a checkbox must never roll back an
      // in-flight drag's bounds on the same overlay.
      await resolveOverlayWriteConflict(
        siteId, id, { visible: newVisible }, pendingOverlayWrites.get(id), 'Overlay visibility', get, set,
      );
    } else {
      console.warn('[V4] Overlay visibility persist failed, rolling back:', err);
      const snapshot = pendingOverlayWrites.get(id);
      if (snapshot) {
        const snapVisible = snapshot.visible;
        set((state: any) => ({
          data: {
            ...state.data,
            overlays: state.data.overlays.map((o: Overlay) =>
              o.id === id ? { ...o, visible: snapVisible } : o
            ),
          },
        }));
      }
    }
    pendingOverlayWrites.delete(id);
  } finally {
    overlayVisibilityInFlight.delete(id);
    if (overlayVisibilityPending.has(id)) {
      const nextVisible = overlayVisibilityPending.get(id)!;
      overlayVisibilityPending.delete(id);
      void flushOverlayVisibility(id, nextVisible, get, set);
    }
  }
}

async function flushOrthophotoVisibility(
  id: string,
  newVisible: boolean,
  get: () => any,
  set: (updater: any) => void,
): Promise<void> {
  const siteId = get().meta.siteId;
  const ortho = get().data.orthophotos.find((o: Orthophoto) => o.id === id);
  const version = ortho?._version;
  if (!siteId || !version) return;

  if (ortho && !pendingOrthophotoWrites.has(id)) pendingOrthophotoWrites.set(id, ortho);
  orthoVisibilityInFlight.add(id);

  try {
    const updated = await updateEntityOrthophoto(siteId, id, { visible: newVisible }, version);
    pendingOrthophotoWrites.delete(id);
    set((state: any) => ({
      data: {
        ...state.data,
        orthophotos: state.data.orthophotos.map((o: Orthophoto) =>
          o.id === id ? { ...o, _version: updated._version } : o
        ),
      },
    }));
  } catch (err) {
    if (isSpatialConflictError(err)) {
      console.warn('[V4 CONFLICT] Orthophoto visibility conflict, reloading:', id);
      const serverOp = await fetchSingleEntityOrthophoto(siteId, id);
      if (serverOp) {
        set((state: any) => ({
          data: {
            ...state.data,
            orthophotos: state.data.orthophotos.map((o: Orthophoto) => (o.id === id ? serverOp : o)),
          },
        }));
      }
    } else {
      console.warn('[V4] Orthophoto visibility persist failed, rolling back:', err);
      const snapshot = pendingOrthophotoWrites.get(id);
      if (snapshot) {
        const snapVisible = snapshot.visible;
        set((state: any) => ({
          data: {
            ...state.data,
            orthophotos: state.data.orthophotos.map((o: Orthophoto) =>
              o.id === id ? { ...o, visible: snapVisible } : o
            ),
          },
        }));
      }
    }
    pendingOrthophotoWrites.delete(id);
  } finally {
    orthoVisibilityInFlight.delete(id);
    if (orthoVisibilityPending.has(id)) {
      const nextVisible = orthoVisibilityPending.get(id)!;
      orthoVisibilityPending.delete(id);
      void flushOrthophotoVisibility(id, nextVisible, get, set);
    }
  }
}

function getConfirmedOverlays(overlays: Overlay[]): Overlay[] {
  if (pendingOverlayWrites.size === 0) return overlays;
  const result: Overlay[] = [];
  for (const ov of overlays) {
    if (!pendingOverlayWrites.has(ov.id)) {
      result.push(ov);
    } else {
      const snapshot = pendingOverlayWrites.get(ov.id) ?? null;
      if (snapshot === null) continue; // pending add
      result.push(snapshot); // pending update — use pre-mutation snapshot
    }
  }
  for (const [, snapshot] of pendingOverlayWrites) {
    if (snapshot !== null && snapshot !== undefined && !result.some((o) => o.id === snapshot.id)) {
      result.push(snapshot); // pending delete — re-include unconfirmed removal
    }
  }
  return result;
}

function getConfirmedOrthophotos(orthophotos: Orthophoto[]): Orthophoto[] {
  if (pendingOrthophotoWrites.size === 0) return orthophotos;
  const result: Orthophoto[] = [];
  for (const op of orthophotos) {
    if (!pendingOrthophotoWrites.has(op.id)) {
      result.push(op);
    } else {
      const snapshot = pendingOrthophotoWrites.get(op.id) ?? null;
      if (snapshot === null) continue; // pending add
      result.push(snapshot); // pending update — use pre-mutation snapshot
    }
  }
  for (const [, snapshot] of pendingOrthophotoWrites) {
    if (snapshot !== null && snapshot !== undefined && !result.some((o) => o.id === snapshot.id)) {
      result.push(snapshot); // pending delete — re-include unconfirmed removal
    }
  }
  return result;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  meta: defaultMeta,
  data: defaultData,
  selectedDate: null,
  availableDates: [],
  visibleDates: [],
  activeLayoutId: null,

  setMeta: (meta) =>
    set((state) => ({ meta: { ...state.meta, ...meta } })),

  setData: (data) =>
    set((state) => ({
      data: { ...state.data, ...data },
      meta: { ...state.meta, isModified: true },
    })),

  markModified: () =>
    set((state) => ({ meta: { ...state.meta, isModified: true } })),

  markSaved: () =>
    set((state) => ({
      meta: { ...state.meta, isModified: false, lastSaved: new Date().toISOString() },
    })),

  setSaveStatus: (status) =>
    set((state) => {
      // Conflict is sticky — only explicit conflict resolution (P0.8) can clear it
      if (state.meta.saveStatus === 'conflict' && status !== 'conflict') return state;
      return { meta: { ...state.meta, saveStatus: status } };
    }),

  // Indications
  addIndication: (indication) => {
    // F-02E-2B: share viewers never persist entity writes. Keep the optimistic
    // local add (so the UI reflects it) but skip the isModified flag and the
    // server write — the backend rejects share-visitor writes anyway, so this
    // avoids the 401 + rollback churn and a misleading "Modified" badge.
    // FL-2 exception: FIELD-kind visitors DO take the server write below
    // (createEntityIndication → backend stamps createdVia provenance), while
    // still skipping the isModified flag like every share-mode path.
    const fieldMode = isFieldLinkActive();
    const inShareViewer = isShareViewerActive();
    // Optimistic local update
    set((state) => ({
      data: { ...state.data, indications: [...state.data.indications, indication] },
      meta: inShareViewer ? state.meta : { ...state.meta, isModified: true },
    }));
    if (inShareViewer && !fieldMode) return;
    // V4 P2.5: Entity API is sole write authority for server-backed projects.
    // pendingEntityWrites guard prevents blob autosave from serializing
    // unconfirmed optimistic state during the in-flight window.
    const siteId = get().meta.siteId;
    if (siteId) {
      pendingEntityWrites.set(indication.id, null); // null = pending add (didn't exist)
      createEntityIndication(siteId, indication)
        .then((created) => {
          pendingEntityWrites.delete(indication.id); // confirmed
          // Success: update _version from server response. Field mode adopts
          // the full server row — the backend stamped createdVia +
          // lastFieldChangeAt into properties, and the "Field changes" lens
          // reads them from local state.
          set((state) => ({
            data: {
              ...state.data,
              indications: state.data.indications.map((ind) =>
                ind.id === indication.id
                  ? (fieldMode ? created : { ...ind, _version: created._version })
                  : ind
              ),
            },
          }));
        })
        .catch((err) => {
          // Entity write failed — rollback: remove optimistically-added indication.
          console.warn('[V4] Entity create failed, rolling back optimistic add:', err);
          set((state) => ({
            data: {
              ...state.data,
              indications: state.data.indications.filter((ind) => ind.id !== indication.id),
            },
          }));
          pendingEntityWrites.delete(indication.id); // cleanup after rollback
        });
    }
  },

  addIndicationsBulk: (indications) => {
    if (indications.length === 0) {
      return Promise.resolve({ createdIds: [], conflictIds: [], failedIds: [] });
    }
    // F-02E-2B: share viewers never reach this (panel is hidden), but guard
    // anyway — refuse outright rather than creating phantom local indications.
    if (isShareViewerActive()) {
      return Promise.resolve({
        createdIds: [],
        conflictIds: [],
        failedIds: indications.map((i) => i.id),
      });
    }

    // ONE optimistic set() for the whole batch (a per-item loop would fire a
    // re-render per row — a 500-ghost accept would freeze the map).
    set((state) => ({
      data: { ...state.data, indications: [...state.data.indications, ...indications] },
      meta: { ...state.meta, isModified: true },
    }));

    const siteId = get().meta.siteId;
    if (!siteId) {
      // Local (blob-only) project — the local add is authoritative.
      return Promise.resolve({
        createdIds: indications.map((i) => i.id),
        conflictIds: [],
        failedIds: [],
      });
    }

    // Guard blob autosave from serializing the unconfirmed adds (same
    // pendingEntityWrites protocol as addIndication).
    for (const ind of indications) pendingEntityWrites.set(ind.id, null);

    const BULK_CHUNK = 200; // server hard limit per /indications/bulk request
    const run = async () => {
      const createdIds: string[] = [];
      const conflictIds: string[] = [];
      const failedIds: string[] = [];

      const rollbackLocally = (ids: string[]) => {
        if (ids.length === 0) return;
        const gone = new Set(ids);
        set((state) => ({
          data: {
            ...state.data,
            indications: state.data.indications.filter((ind) => !gone.has(ind.id)),
          },
        }));
        for (const id of ids) pendingEntityWrites.delete(id);
      };

      // Sequential chunks (mirrors the batch-apply loop in IndicationsTicket) —
      // parallel bulk POSTs are exactly the write-drop shape the undo/history
      // incident came from.
      for (let i = 0; i < indications.length; i += BULK_CHUNK) {
        const chunk = indications.slice(i, i + BULK_CHUNK);
        try {
          const res = await bulkCreateEntityIndications(siteId, chunk);
          // The endpoint returns counts + conflict arrays only — NO created
          // list. Contract: every sent id absent from conflicts AND
          // staleConflicts was INSERTed with version=1. Stamp `_version: 1`
          // on exactly those ids; skipping the stamp would make every later
          // edit of these indications silently local-only (D1 is the sole
          // entity authority — the edit would vanish on reload).
          const rejected = new Set<string>([
            ...res.conflicts.map((c) => c.id),
            ...res.staleConflicts.map((c) => c.id),
          ]);
          const confirmed = chunk.filter((ind) => !rejected.has(ind.id));
          const confirmedIdSet = new Set(confirmed.map((ind) => ind.id));
          set((state) => ({
            data: {
              ...state.data,
              indications: state.data.indications.map((ind) =>
                confirmedIdSet.has(ind.id) ? { ...ind, _version: 1 } : ind
              ),
            },
          }));
          for (const ind of confirmed) {
            pendingEntityWrites.delete(ind.id);
            createdIds.push(ind.id);
          }
          // Conflicted ids (already exist server-side / stale) never landed —
          // roll their optimistic adds back out of the local store.
          const chunkConflicts = chunk
            .filter((ind) => rejected.has(ind.id))
            .map((ind) => ind.id);
          rollbackLocally(chunkConflicts);
          conflictIds.push(...chunkConflicts);
        } catch (err) {
          // Transport/validation failure — nothing in this chunk is known to
          // exist server-side. Roll the whole chunk back (honest counts; the
          // caller re-offers the ghosts for another try).
          console.warn('[detection] bulk indication create failed, rolling back chunk:', err);
          const ids = chunk.map((ind) => ind.id);
          rollbackLocally(ids);
          failedIds.push(...ids);
        }
      }
      return { createdIds, conflictIds, failedIds };
    };

    // Tracked so flushPendingEntityWrites (Save to Server) waits for the
    // _version stamps to land before serializing.
    return trackEntityPromise(run());
  },

  applyServerIndicationRows: (rows) => {
    const byId = new Map(rows.map((r) => [r.id, r]));
    set((state) => ({
      data: {
        ...state.data,
        indications: state.data.indications.map((ind) => {
          const r = byId.get(ind.id);
          if (!r) return ind;
          return {
            ...ind,
            ...(r.geometry ? { geometry: r.geometry } : {}),
            properties: r.properties ? { ...ind.properties, ...r.properties } : ind.properties,
            _version: r._version,
          };
        }),
      },
    }));
  },

  replaceIndications: (indications) => {
    set((state) => ({ data: { ...state.data, indications } }));
    get().updateAvailableDates();
  },

  updateIndication: (id, updates) => {
    const existing = get().data.indications.find((i) => i.id === id);
    // Cross-inspection invariant: root thermalData mirrors the LATEST
    // observation (utils/observations.ts). Popups read thermal STRICTLY from
    // the active observation, so a root-only thermal write on a fault with an
    // explicit history would vanish on reopen — write it through to
    // observations[last] too. Callers that manage observations themselves
    // (classify link path) stay untouched.
    if (updates.properties && 'thermalData' in updates.properties && !('observations' in updates.properties)) {
      const obs = existing?.properties.observations;
      if (obs && obs.length > 0) {
        const synced = [...obs];
        synced[synced.length - 1] = { ...synced[synced.length - 1], thermalData: updates.properties.thermalData };
        updates = { ...updates, properties: { ...updates.properties, observations: synced } };
      }
    }
    const mergedProperties = { ...existing?.properties, ...updates.properties, updatedAt: new Date().toISOString() };
    // F-02E-2B: share viewers never persist entity writes (see addIndication).
    // Keeps the optimistic local update; skips isModified + the server PATCH.
    const inShareViewer = isShareViewerActive();
    // Optimistic local update
    set((state) => ({
      data: {
        ...state.data,
        indications: state.data.indications.map((ind) =>
          ind.id === id
            ? { ...ind, ...updates, properties: { ...ind.properties, ...updates.properties, updatedAt: new Date().toISOString() } }
            : ind
        ),
      },
      meta: inShareViewer ? state.meta : { ...state.meta, isModified: true },
    }));
    if (inShareViewer) return;
    // V4 P2.5: Entity API is sole write authority for server-backed projects.
    // pendingEntityWrites guard prevents blob autosave from serializing
    // unconfirmed optimistic state during the in-flight window.
    const siteId = get().meta.siteId;
    const version = existing?._version;
    if (siteId && version) {
      if (!pendingEntityWrites.has(id)) {
        pendingEntityWrites.set(id, existing!); // pre-mutation snapshot (only first in chain)
      }
      updateEntityIndication(siteId, id, {
        type: updates.type,
        geometry: updates.geometry,
        properties: mergedProperties,
      }, version)
        .then((updated) => {
          pendingEntityWrites.delete(id); // confirmed
          // Success: update _version from server response
          set((state) => ({
            data: {
              ...state.data,
              indications: state.data.indications.map((ind) =>
                ind.id === id ? { ...ind, _version: updated._version } : ind
              ),
            },
          }));
        })
        .catch(async (err) => {
          if (isConflictError(err)) {
            // 409 VERSION_CONFLICT: fetch authoritative server state to replace
            // the stale optimistic update.
            console.warn('[V4 CONFLICT] Indication update conflict, reloading server state:', id);
            const serverInd = await fetchSingleEntityIndication(siteId, id);
            if (serverInd) {
              set((state) => ({
                data: {
                  ...state.data,
                  indications: state.data.indications.map((ind) =>
                    ind.id === id ? serverInd : ind
                  ),
                },
              }));
            }
          } else {
            // Transient error — rollback to pre-update state.
            console.warn('[V4] Entity update failed, rolling back optimistic update:', err);
            if (existing) {
              set((state) => ({
                data: {
                  ...state.data,
                  indications: state.data.indications.map((ind) =>
                    ind.id === id ? existing : ind
                  ),
                },
              }));
            }
          }
          pendingEntityWrites.delete(id); // cleanup after rollback
        });
    }
  },

  setIndicationStatus: (id, status, note) => {
    // Viewers never mutate (the backend also 403s; this is the client guard).
    // FL-1 exception: a FIELD-kind share link may transition status — the
    // backend's shareFieldWritable path enforces the rest (actor header,
    // rate limit, allowlist).
    const fieldMode = isFieldLinkActive();
    if (isShareViewerActive() && !fieldMode) return Promise.resolve('noop');
    const existing = get().data.indications.find((i) => i.id === id);
    if (!existing) return Promise.resolve('noop');
    const siteId = get().meta.siteId;
    const version = existing._version;
    // Field mode has no blob fallback: without an entity version there is
    // nothing the visitor could persist — fail before the optimistic flip.
    if (fieldMode && (!siteId || !version)) return Promise.resolve('error');
    // Optimistic: flip status immediately for a snappy UI. The authoritative
    // statusEvents (server-stamped at/by) arrive in the response and replace
    // the local indication wholesale on success.
    set((state) => ({
      data: {
        ...state.data,
        indications: state.data.indications.map((ind) =>
          ind.id === id ? { ...ind, properties: { ...ind.properties, status } } : ind
        ),
      },
      // FL-1: field mode persists server-side immediately — never flip the
      // share viewer into a phantom "Modified/unsaved" state (matches the
      // deliberate share-mode isModified skips elsewhere in this store).
      meta: fieldMode ? state.meta : { ...state.meta, isModified: true },
    }));
    // Entity-backed sites only (need a _version for If-Match). Un-migrated
    // blob sites keep the optimistic flip but can't persist status yet.
    if (!siteId || !version) return Promise.resolve('local');
    if (!pendingEntityWrites.has(id)) {
      pendingEntityWrites.set(id, existing);
    }
    return updateEntityIndicationStatus(siteId, id, status, version, note)
      .then((updated): 'updated' => {
        pendingEntityWrites.delete(id);
        set((state) => ({
          data: {
            ...state.data,
            indications: state.data.indications.map((ind) =>
              ind.id === id ? updated : ind
            ),
          },
        }));
        return 'updated';
      })
      .catch(async (err): Promise<'conflict' | 'error'> => {
        if (isConflictError(err)) {
          // 409: reload authoritative server state over the stale optimistic flip.
          console.warn('[V4 CONFLICT] Indication status conflict, reloading server state:', id);
          const serverInd = await fetchSingleEntityIndication(siteId, id);
          if (serverInd) {
            set((state) => ({
              data: {
                ...state.data,
                indications: state.data.indications.map((ind) =>
                  ind.id === id ? serverInd : ind
                ),
              },
            }));
          }
          pendingEntityWrites.delete(id);
          return 'conflict';
        }
        // Transient error — roll back to the pre-transition indication.
        console.warn('[V4] Indication status update failed, rolling back:', err);
        set((state) => ({
          data: {
            ...state.data,
            indications: state.data.indications.map((ind) =>
              ind.id === id ? existing : ind
            ),
          },
        }));
        pendingEntityWrites.delete(id);
        return 'error';
      });
  },

  setIndicationFieldNotes: (id, description) => {
    // FL-1 only: everyone else edits notes through the normal indication
    // update path. Field mode requires an entity version (no blob fallback).
    if (!isFieldLinkActive()) return Promise.resolve('noop');
    const existing = get().data.indications.find((i) => i.id === id);
    if (!existing) return Promise.resolve('noop');
    const siteId = get().meta.siteId;
    const version = existing._version;
    if (!siteId || !version) return Promise.resolve('error');

    return updateEntityIndicationFieldNotes(siteId, id, description, version)
      .then((updated): 'updated' => {
        set((state) => ({
          data: {
            ...state.data,
            indications: state.data.indications.map((ind) =>
              ind.id === id ? updated : ind
            ),
          },
        }));
        return 'updated';
      })
      .catch(async (err): Promise<'conflict' | 'error'> => {
        if (isConflictError(err)) {
          const serverInd = await fetchSingleEntityIndication(siteId, id);
          if (serverInd) {
            set((state) => ({
              data: {
                ...state.data,
                indications: state.data.indications.map((ind) =>
                  ind.id === id ? serverInd : ind
                ),
              },
            }));
          }
          return 'conflict';
        }
        console.warn('[FL-1] Field notes update failed:', err);
        return 'error';
      });
  },

  addIndicationFieldPhoto: (id, photo, fileName) => {
    // FL-3a, field links only. NO _version guard: the attach endpoint has no
    // If-Match (server-side commutative append) — an unconfirmed-but-created
    // indication can still receive its photo.
    if (!isFieldLinkActive()) return Promise.resolve('noop');
    const existing = get().data.indications.find((i) => i.id === id);
    if (!existing) return Promise.resolve('noop');
    const siteId = get().meta.siteId;
    if (!siteId) return Promise.resolve('error');

    return addEntityIndicationFieldPhoto(siteId, id, photo, fileName)
      .then((updated): 'updated' => {
        set((state) => ({
          data: {
            ...state.data,
            indications: state.data.indications.map((ind) =>
              ind.id === id ? updated : ind
            ),
          },
        }));
        return 'updated';
      })
      .catch(async (err): Promise<'conflict' | 'limit' | 'error'> => {
        if (err instanceof Error && (err as Error & { code?: string }).code === 'PHOTO_LIMIT_REACHED') {
          return 'limit';
        }
        if (isConflictError(err)) {
          const serverInd = await fetchSingleEntityIndication(siteId, id);
          if (serverInd) {
            set((state) => ({
              data: {
                ...state.data,
                indications: state.data.indications.map((ind) =>
                  ind.id === id ? serverInd : ind
                ),
              },
            }));
          }
          return 'conflict';
        }
        console.warn('[FL-3a] Field photo attach failed:', err);
        return 'error';
      });
  },

  removeIndicationFieldPhoto: (id, photoId) => {
    if (!isFieldLinkActive()) return Promise.resolve('noop');
    const existing = get().data.indications.find((i) => i.id === id);
    if (!existing) return Promise.resolve('noop');
    const siteId = get().meta.siteId;
    const version = existing._version;
    if (!siteId || !version) return Promise.resolve('error');

    return removeEntityIndicationFieldPhoto(siteId, id, photoId, version)
      .then((updated): 'updated' => {
        set((state) => ({
          data: {
            ...state.data,
            indications: state.data.indications.map((ind) =>
              ind.id === id ? updated : ind
            ),
          },
        }));
        return 'updated';
      })
      .catch(async (err): Promise<'conflict' | 'error'> => {
        if (isConflictError(err)) {
          const serverInd = await fetchSingleEntityIndication(siteId, id);
          if (serverInd) {
            set((state) => ({
              data: {
                ...state.data,
                indications: state.data.indications.map((ind) =>
                  ind.id === id ? serverInd : ind
                ),
              },
            }));
          }
          return 'conflict';
        }
        console.warn('[FL-3a] Field photo removal failed:', err);
        return 'error';
      });
  },

  setIndicationFieldGeometry: (id, geometry, topologyUpdates) => {
    if (!isFieldLinkActive()) return Promise.resolve('noop');
    const existing = get().data.indications.find((i) => i.id === id);
    if (!existing) return Promise.resolve('noop');
    const linkId = useShareSessionStore.getState().view?.id;
    if (!linkId || existing.properties.createdVia?.linkId !== linkId) {
      // Not our creation — the draw mode shouldn't have allowed the drag;
      // never send a write the backend will 403.
      return Promise.resolve('noop');
    }
    const siteId = get().meta.siteId;
    const version = existing._version;
    if (!siteId || !version) return Promise.resolve('error');

    // Optimistic: the drag already moved the feature on the map; mirror it
    // (topology fields too — the server response replaces this wholesale).
    set((state) => ({
      data: {
        ...state.data,
        indications: state.data.indications.map((ind) =>
          ind.id === id
            ? {
                ...ind,
                geometry,
                ...(topologyUpdates
                  ? { properties: { ...ind.properties, ...topologyUpdates } }
                  : {}),
              }
            : ind
        ),
      },
    }));

    return updateEntityIndicationFieldGeometry(siteId, id, geometry, version, topologyUpdates)
      .then((updated): 'updated' => {
        set((state) => ({
          data: {
            ...state.data,
            indications: state.data.indications.map((ind) =>
              ind.id === id ? updated : ind
            ),
          },
        }));
        return 'updated';
      })
      .catch(async (err): Promise<'conflict' | 'error'> => {
        if (isConflictError(err)) {
          const serverInd = await fetchSingleEntityIndication(siteId, id);
          if (serverInd) {
            set((state) => ({
              data: {
                ...state.data,
                indications: state.data.indications.map((ind) =>
                  ind.id === id ? serverInd : ind
                ),
              },
            }));
          }
          return 'conflict';
        }
        // Roll the optimistic move back.
        console.warn('[FL-2.5] Field geometry update failed, rolling back:', err);
        set((state) => ({
          data: {
            ...state.data,
            indications: state.data.indications.map((ind) =>
              ind.id === id ? existing : ind
            ),
          },
        }));
        return 'error';
      });
  },

  removeIndicationAsField: (id) => {
    if (!isFieldLinkActive()) return Promise.resolve('noop');
    const existing = get().data.indications.find((i) => i.id === id);
    if (!existing) return Promise.resolve('noop');
    const linkId = useShareSessionStore.getState().view?.id;
    if (!linkId || existing.properties.createdVia?.linkId !== linkId) {
      return Promise.resolve('noop');
    }
    const siteId = get().meta.siteId;
    const version = existing._version;
    if (!siteId || !version) return Promise.resolve('error');

    // Optimistic removal; restore on failure.
    set((state) => ({
      data: {
        ...state.data,
        indications: state.data.indications.filter((ind) => ind.id !== id),
      },
    }));
    useMapStore.getState().setPreviewIndication(null);

    return deleteEntityIndication(siteId, id, version)
      .then((): 'deleted' => 'deleted')
      .catch(async (err): Promise<'conflict' | 'error'> => {
        if (isConflictError(err)) {
          const serverInd = await fetchSingleEntityIndication(siteId, id);
          set((state) => ({
            data: {
              ...state.data,
              indications: serverInd
                ? [...state.data.indications, serverInd]
                : state.data.indications,
            },
          }));
          return 'conflict';
        }
        console.warn('[FL-2.5] Field delete failed, restoring locally:', err);
        set((state) => ({
          data: {
            ...state.data,
            indications: [...state.data.indications, existing],
          },
        }));
        return 'error';
      });
  },

  removeIndicationsLocal: (ids) => {
    if (isShareViewerActive()) return;
    const idSet = new Set(ids);
    set((state) => ({
      data: {
        ...state.data,
        indications: state.data.indications.filter((i) => !idSet.has(i.id)),
      },
      meta: { ...state.meta, isModified: true },
    }));
  },

  removeIndication: (id) => {
    // F-02E-2B: a share viewer must never remove an indication — not even
    // locally, and never schedule an R2 asset deletion. Full no-op for viewers.
    if (isShareViewerActive()) return;
    const ind = get().data.indications.find((i) => i.id === id);
    if (ind) {
      // Don't delete R2 assets for field uploads — those are managed by the
      // mobile uploads panel via handleDeleteFieldUpload. The indication's
      // image URL contains /uploads/ for field upload assets.
      const url = ind.properties.image || '';
      const isFieldUpload = url.includes('/uploads/') || id.startsWith('field-');
      if (!isFieldUpload) {
        scheduleAssetDeletion(url);
      }
    }
    // Optimistic local update
    set((state) => ({
      data: { ...state.data, indications: state.data.indications.filter((i) => i.id !== id) },
      meta: { ...state.meta, isModified: true },
    }));
    // V4 P2.5: Entity API is sole write authority for server-backed projects.
    // pendingEntityWrites guard prevents blob autosave from serializing
    // unconfirmed optimistic state during the in-flight window.
    const siteId = get().meta.siteId;
    const version = ind?._version;
    if (siteId && version) {
      if (!pendingEntityWrites.has(id)) {
        pendingEntityWrites.set(id, ind!); // pre-removal snapshot
      }
      deleteEntityIndication(siteId, id, version)
        .then(() => {
          pendingEntityWrites.delete(id); // confirmed
        })
        .catch(async (err) => {
          if (isConflictError(err)) {
            // 409 VERSION_CONFLICT: indication was modified by another session.
            // Fetch authoritative server state and re-add to local store.
            console.warn('[V4 CONFLICT] Indication delete conflict, restoring server state:', id);
            const serverInd = await fetchSingleEntityIndication(siteId, id);
            if (serverInd) {
              set((state) => ({
                data: {
                  ...state.data,
                  indications: [...state.data.indications, serverInd],
                },
              }));
            }
          } else {
            // Transient error — rollback: re-add original indication.
            console.warn('[V4] Entity delete failed, rolling back optimistic removal:', err);
            if (ind) {
              set((state) => ({
                data: {
                  ...state.data,
                  indications: [...state.data.indications, ind],
                },
              }));
            }
          }
          pendingEntityWrites.delete(id); // cleanup after rollback
        });
    }
  },

  getIndicationsByType: (type) => get().data.indications.filter((ind) => ind.type === type),

  getIndicationsByDate: (date) =>
    get().data.indications.filter((ind) => ind.properties.captureDate === date),

  // Overlays — V4 P2.7: Entity API write switch
  addOverlay: (overlay) => {
    // Optimistic local update
    set((state) => ({
      data: { ...state.data, overlays: [...state.data.overlays, overlay] },
      meta: { ...state.meta, isModified: true },
    }));
    const siteId = get().meta.siteId;
    if (siteId) {
      pendingOverlayWrites.set(overlay.id, null); // null = pending add
      trackEntityPromise(
        createEntityOverlay(siteId, overlay)
          .then((created) => {
            pendingOverlayWrites.delete(overlay.id);
            set((state) => ({
              data: {
                ...state.data,
                overlays: state.data.overlays.map((o) =>
                  o.id === overlay.id ? { ...o, _version: created._version } : o
                ),
              },
            }));
          })
          .catch((err) => {
            console.warn('[V4] Entity overlay create failed, rolling back:', err);
            set((state) => ({
              data: {
                ...state.data,
                overlays: state.data.overlays.filter((o) => o.id !== overlay.id),
              },
            }));
            pendingOverlayWrites.delete(overlay.id);
          })
      );
    }
  },

  updateOverlay: (id, updates) => {
    const existing = get().data.overlays.find((o) => o.id === id);
    // Optimistic local update
    set((state) => ({
      data: {
        ...state.data,
        overlays: state.data.overlays.map((o) => (o.id === id ? { ...o, ...updates } : o)),
      },
      meta: { ...state.meta, isModified: true },
    }));
    const siteId = get().meta.siteId;
    const version = existing?._version;
    if (siteId && version) {
      if (!pendingOverlayWrites.has(id)) {
        pendingOverlayWrites.set(id, existing!);
      }
      trackEntityPromise(
        updateEntityOverlay(siteId, id, updates, version)
          .then((updated) => {
            pendingOverlayWrites.delete(id);
            set((state) => ({
              data: {
                ...state.data,
                overlays: state.data.overlays.map((o) =>
                  o.id === id ? { ...o, _version: updated._version } : o
                ),
              },
            }));
          })
          .catch(async (err) => {
            if (isSpatialConflictError(err)) {
              // A 409 here is nearly always THIS tab racing itself: dragging an
              // overlay writes once per dragend (useOverlayMarkers), so an
              // edge-by-edge re-fit issues a burst and two bind the same
              // version. See resolveOverlayWriteConflict for why the old
              // "replace the local row with a server re-read" was harmful.
              await resolveOverlayWriteConflict(
                siteId, id, updates, existing, 'Overlay update', get, set,
              );
            } else {
              // Roll back ONLY the fields this write was carrying. Restoring the
              // whole `existing` snapshot would also revert unrelated changes
              // made since — e.g. a failed tileStatus write undoing the user's
              // bounds.
              console.warn('[V4] Entity overlay update failed, rolling back:', err);
              if (existing) {
                const revert: Partial<Overlay> = {};
                for (const key of Object.keys(updates) as (keyof Overlay)[]) {
                  (revert as Record<string, unknown>)[key] = existing[key];
                }
                set((state) => ({
                  data: {
                    ...state.data,
                    overlays: state.data.overlays.map((o) =>
                      o.id === id ? { ...o, ...revert } : o
                    ),
                  },
                }));
              }
            }
            pendingOverlayWrites.delete(id);
          })
      );
    }
  },

  removeOverlay: (id) => {
    const item = get().data.overlays.find((o) => o.id === id);
    if (item) {
      scheduleAssetDeletion(item.url);
      // A plan overlay also owns the source PDF uploaded at import. Deleting
      // only the proxy left the PDF — often the larger of the pair — stranded
      // with nothing referencing it. Passed as a bare key, matching how
      // removeOrthophoto disposes of its own sourceR2Key below.
      if (item.sourceR2Key) scheduleAssetDeletion(item.sourceR2Key);
    }
    // Optimistic local update
    set((state) => ({
      data: { ...state.data, overlays: state.data.overlays.filter((o) => o.id !== id) },
      meta: { ...state.meta, isModified: true },
    }));
    const siteId = get().meta.siteId;
    const version = item?._version;
    if (siteId && version) {
      if (!pendingOverlayWrites.has(id)) {
        pendingOverlayWrites.set(id, item!);
      }
      deleteEntityOverlay(siteId, id, version)
        .then(() => {
          pendingOverlayWrites.delete(id);
        })
        .catch(async (err) => {
          if (isSpatialConflictError(err)) {
            console.warn('[V4 CONFLICT] Overlay delete conflict, restoring:', id);
            const serverOv = await fetchSingleEntityOverlay(siteId, id);
            if (serverOv) {
              set((state) => ({
                data: { ...state.data, overlays: [...state.data.overlays, serverOv] },
              }));
            }
          } else {
            console.warn('[V4] Entity overlay delete failed, rolling back:', err);
            if (item) {
              set((state) => ({
                data: { ...state.data, overlays: [...state.data.overlays, item] },
              }));
            }
          }
          pendingOverlayWrites.delete(id);
        });
    }
  },

  setOverlayOpacity: (id, opacity) => {
    // Capture the pre-mutation snapshot synchronously BEFORE the optimistic
    // set() so exportProject()'s confirmed-state guard engages immediately
    // (within the debounce window) and the non-conflict catch in
    // flushOverlayOpacity has the original opacity to roll back to. The
    // !has(id) guard preserves the first pre-drag baseline across multi-tick
    // slider drags. Mirrors toggleOverlayVisibility's pattern.
    const existing = get().data.overlays.find((o) => o.id === id);
    const siteId = get().meta.siteId;
    const inShareViewer = isShareViewerActive();
    if (existing && siteId && existing._version && !pendingOverlayWrites.has(id) && !inShareViewer) {
      pendingOverlayWrites.set(id, existing);
    }

    // Optimistic local update — immediate visual feedback, drives Mapbox paint.
    set((state) => ({
      data: {
        ...state.data,
        overlays: state.data.overlays.map((o) => (o.id === id ? { ...o, opacity } : o)),
      },
    }));

    // F-02E-2B: share visitor → local-only slider, no PATCH scheduled.
    if (inShareViewer) return;

    // Trailing-edge debounce + per-id in-flight serialization. See module-
    // level comment near OPACITY_DEBOUNCE_MS.
    overlayOpacityPending.set(id, opacity);
    const existingTimer = overlayOpacityTimer.get(id);
    if (existingTimer) clearTimeout(existingTimer);
    overlayOpacityTimer.set(
      id,
      setTimeout(() => {
        overlayOpacityTimer.delete(id);
        flushOverlayOpacity(id, get, set);
      }, OPACITY_DEBOUNCE_MS),
    );
  },

  toggleOverlayVisibility: (id) => {
    const existing = get().data.overlays.find((o) => o.id === id);
    const newVisible = !existing?.visible;
    const siteId = get().meta.siteId;
    const inShareViewer = isShareViewerActive();

    // P-25: snapshot the pre-flip overlay synchronously BEFORE the optimistic
    // set() so exportProject()'s confirmed-state guard returns the old visible
    // value during the PATCH window, and flushOverlayVisibility's non-conflict
    // catch has the correct rollback target. The !has(id) guard preserves the
    // first pre-toggle baseline across coalesced rapid clicks.
    // F-02E-2B: share viewer never enters the PATCH path, so the rollback
    // snapshot would only leak across viewer sessions — skip it.
    if (existing && siteId && existing._version && !pendingOverlayWrites.has(id) && !inShareViewer) {
      pendingOverlayWrites.set(id, existing);
    }

    // Optimistic local flip drives Mapbox paint immediately.
    set((state) => ({
      data: {
        ...state.data,
        overlays: state.data.overlays.map((o) => (o.id === id ? { ...o, visible: !o.visible } : o)),
      },
    }));

    // F-02E-2B: share visitor → local-only toggle, no PATCH, no rollback.
    if (inShareViewer) return;

    const version = existing?._version;
    if (!siteId || !version) return;

    // P-31: if a visibility PATCH is already in flight for this id, only
    // record the latest intent. The in-flight handler's finally block will
    // fire a follow-up PATCH using the fresh _version. This prevents back-
    // to-back PATCHes from binding the same stale _version (which 409s).
    if (overlayVisibilityInFlight.has(id)) {
      overlayVisibilityPending.set(id, newVisible);
      return;
    }
    void flushOverlayVisibility(id, newVisible, get, set);
  },

  // Orthophotos — V4 P2.7: Entity API write switch
  addOrthophoto: (orthophoto) => {
    // New orthophotos land on TOP of the stack (index 0). Lower sortOrder =
    // higher in the visual stack, so a fresh upload gets a value just below
    // the current minimum and is prepended. See Orthophoto.sortOrder.
    const minSort = get().data.orthophotos.reduce((m, o) => Math.min(m, o.sortOrder ?? 0), 0);
    const newOrtho: Orthophoto = {
      ...orthophoto,
      sortOrder: orthophoto.sortOrder ?? minSort - 1,
    };
    // Optimistic local update — prepend so index 0 stays the top layer.
    set((state) => ({
      data: { ...state.data, orthophotos: [newOrtho, ...state.data.orthophotos] },
      meta: { ...state.meta, isModified: true },
    }));
    const siteId = get().meta.siteId;
    if (siteId) {
      pendingOrthophotoWrites.set(newOrtho.id, null); // null = pending add
      trackEntityPromise(
        createEntityOrthophoto(siteId, newOrtho)
          .then((created) => {
            pendingOrthophotoWrites.delete(orthophoto.id);
            set((state) => ({
              data: {
                ...state.data,
                orthophotos: state.data.orthophotos.map((o) =>
                  o.id === orthophoto.id ? { ...o, _version: created._version } : o
                ),
              },
            }));
          })
          .catch((err) => {
            console.warn('[V4] Entity orthophoto create failed, rolling back:', err);
            set((state) => ({
              data: {
                ...state.data,
                orthophotos: state.data.orthophotos.filter((o) => o.id !== orthophoto.id),
              },
            }));
            pendingOrthophotoWrites.delete(orthophoto.id);
          })
      );
    }
  },

  updateOrthophoto: (id, updates) => {
    const existing = get().data.orthophotos.find((o) => o.id === id);
    // Optimistic local update
    set((state) => ({
      data: {
        ...state.data,
        orthophotos: state.data.orthophotos.map((o) => (o.id === id ? { ...o, ...updates } : o)),
      },
      meta: { ...state.meta, isModified: true },
    }));
    const siteId = get().meta.siteId;
    const version = existing?._version;
    if (siteId && version) {
      if (!pendingOrthophotoWrites.has(id)) {
        pendingOrthophotoWrites.set(id, existing!);
      }
      trackEntityPromise(
        updateEntityOrthophoto(siteId, id, updates, version)
          .then((updated) => {
            pendingOrthophotoWrites.delete(id);
            set((state) => ({
              data: {
                ...state.data,
                orthophotos: state.data.orthophotos.map((o) =>
                  o.id === id ? { ...o, _version: updated._version } : o
                ),
              },
            }));
          })
          .catch(async (err) => {
            if (isSpatialConflictError(err)) {
              console.warn('[V4 CONFLICT] Orthophoto update conflict, reloading:', id);
              const serverOp = await fetchSingleEntityOrthophoto(siteId, id);
              if (serverOp) {
                set((state) => ({
                  data: {
                    ...state.data,
                    orthophotos: state.data.orthophotos.map((o) => (o.id === id ? serverOp : o)),
                  },
                }));
              }
            } else {
              console.warn('[V4] Entity orthophoto update failed, rolling back:', err);
              if (existing) {
                set((state) => ({
                  data: {
                    ...state.data,
                    orthophotos: state.data.orthophotos.map((o) => (o.id === id ? existing : o)),
                  },
                }));
              }
            }
            pendingOrthophotoWrites.delete(id);
          })
      );
    }
  },

  removeOrthophoto: (id) => {
    const item = get().data.orthophotos.find((o) => o.id === id);
    if (item) {
      scheduleAssetDeletion(item.url);
      // Clean up source R2 file
      if (item.sourceR2Key) scheduleAssetDeletion(item.sourceR2Key);
      // Clean up stored source file reference
      removeSourceFile(id);
      // Note: tileset R2 cleanup (tiles/{tilesetId}/*) is handled server-side
      // via DELETE /api/v2/sites/:siteId/tilesets/:id when the project saves
    }
    // Optimistic local update
    set((state) => ({
      data: { ...state.data, orthophotos: state.data.orthophotos.filter((o) => o.id !== id) },
      meta: { ...state.meta, isModified: true },
    }));
    const siteId = get().meta.siteId;
    const version = item?._version;
    if (siteId && version) {
      if (!pendingOrthophotoWrites.has(id)) {
        pendingOrthophotoWrites.set(id, item!);
      }
      deleteEntityOrthophoto(siteId, id, version)
        .then(() => {
          pendingOrthophotoWrites.delete(id);
        })
        .catch(async (err) => {
          if (isSpatialConflictError(err)) {
            console.warn('[V4 CONFLICT] Orthophoto delete conflict, restoring:', id);
            const serverOp = await fetchSingleEntityOrthophoto(siteId, id);
            if (serverOp) {
              set((state) => ({
                data: { ...state.data, orthophotos: [...state.data.orthophotos, serverOp] },
              }));
            }
          } else {
            console.warn('[V4] Entity orthophoto delete failed, rolling back:', err);
            if (item) {
              set((state) => ({
                data: { ...state.data, orthophotos: [...state.data.orthophotos, item] },
              }));
            }
          }
          pendingOrthophotoWrites.delete(id);
        });
    }
  },

  setOrthophotoOpacity: (id, opacity) => {
    // Capture the pre-mutation snapshot synchronously BEFORE the optimistic
    // set() — mirrors setOverlayOpacity. The !has(id) guard preserves the
    // first pre-drag baseline across multi-tick slider drags.
    const existing = get().data.orthophotos.find((o) => o.id === id);
    const siteId = get().meta.siteId;
    const inShareViewer = isShareViewerActive();
    if (existing && siteId && existing._version && !pendingOrthophotoWrites.has(id) && !inShareViewer) {
      pendingOrthophotoWrites.set(id, existing);
    }

    // Optimistic local update — immediate visual feedback. Share viewers
    // skip the isModified flag so the "● Modified" project-dirty indicator
    // doesn't appear from a read-only legend interaction (F-02E-2B).
    set((state) => (
      inShareViewer
        ? {
            data: {
              ...state.data,
              orthophotos: state.data.orthophotos.map((o) => (o.id === id ? { ...o, opacity } : o)),
            },
          }
        : {
            data: {
              ...state.data,
              orthophotos: state.data.orthophotos.map((o) => (o.id === id ? { ...o, opacity } : o)),
            },
            meta: { ...state.meta, isModified: true },
          }
    ));

    // F-02E-2B: share visitor → local-only slider, no PATCH scheduled.
    if (inShareViewer) return;

    // Trailing-edge debounce + per-id in-flight serialization (matches overlay).
    orthoOpacityPending.set(id, opacity);
    const existingTimer = orthoOpacityTimer.get(id);
    if (existingTimer) clearTimeout(existingTimer);
    orthoOpacityTimer.set(
      id,
      setTimeout(() => {
        orthoOpacityTimer.delete(id);
        flushOrthophotoOpacity(id, get, set);
      }, OPACITY_DEBOUNCE_MS),
    );
  },

  toggleOrthophotoVisibility: (id) => {
    const existing = get().data.orthophotos.find((o) => o.id === id);
    const newVisible = !existing?.visible;
    const siteId = get().meta.siteId;
    const inShareViewer = isShareViewerActive();

    // P-25: snapshot pre-flip before the optimistic set() — mirrors the
    // overlay toggle setter above. Skip for share viewers since no PATCH
    // will fire and the snapshot would only confuse exportProject().
    if (existing && siteId && existing._version && !pendingOrthophotoWrites.has(id) && !inShareViewer) {
      pendingOrthophotoWrites.set(id, existing);
    }

    // Optimistic local flip. Authenticated path keeps the legacy
    // meta.isModified=true side-effect; share viewer skips it so the legend
    // doesn't flip the project-dirty indicator (F-02E-2B).
    set((state) => (
      inShareViewer
        ? {
            data: {
              ...state.data,
              orthophotos: state.data.orthophotos.map((o) => (o.id === id ? { ...o, visible: !o.visible } : o)),
            },
          }
        : {
            data: {
              ...state.data,
              orthophotos: state.data.orthophotos.map((o) => (o.id === id ? { ...o, visible: !o.visible } : o)),
            },
            meta: { ...state.meta, isModified: true },
          }
    ));

    // F-02E-2B: share visitor → local-only toggle, no PATCH, no rollback.
    if (inShareViewer) return;

    const version = existing?._version;
    if (!siteId || !version) return;

    // P-31: coalesce rapid clicks during an in-flight PATCH. See the matching
    // overlay path above for the full rationale.
    if (orthoVisibilityInFlight.has(id)) {
      orthoVisibilityPending.set(id, newVisible);
      return;
    }
    void flushOrthophotoVisibility(id, newVisible, get, set);
  },

  moveOrthophoto: (id, direction) => {
    const orthos = get().data.orthophotos;
    const i = orthos.findIndex((o) => o.id === id);
    if (i === -1) return;
    const target = direction === 'up' ? i - 1 : i + 1;
    if (target < 0 || target >= orthos.length) return; // already at an end

    // Swap positions, then normalize sortOrder = array index (0 = top). A full
    // reindex keeps values distinct even when legacy rows all share
    // sort_order=0, so the new order round-trips through the ASC-sorted list API.
    const reordered = orthos.slice();
    [reordered[i], reordered[target]] = [reordered[target], reordered[i]];
    const withOrder = reordered.map((o, idx) => ({ ...o, sortOrder: idx }));

    set((state) => ({
      data: { ...state.data, orthophotos: withOrder },
      meta: { ...state.meta, isModified: true },
    }));

    // Persist only the rows whose sortOrder actually changed. updateOrthophoto
    // handles the per-row D1 PATCH + version bump; local-only projects (no
    // siteId/_version) simply keep the reordered array (persisted via blob).
    const prevOrder = new Map(orthos.map((o) => [o.id, o.sortOrder ?? 0]));
    for (const o of withOrder) {
      if (prevOrder.get(o.id) !== o.sortOrder) {
        get().updateOrthophoto(o.id, { sortOrder: o.sortOrder });
      }
    }
  },

  // Flight paths
  addFlightPath: (flightPath) =>
    set((state) => ({
      data: { ...state.data, flightPaths: [...state.data.flightPaths, flightPath] },
      meta: { ...state.meta, isModified: true },
    })),

  updateFlightPath: (id, updates) =>
    set((state) => ({
      data: {
        ...state.data,
        flightPaths: state.data.flightPaths.map((f) => (f.id === id ? { ...f, ...updates } : f)),
      },
      meta: { ...state.meta, isModified: true },
    })),

  removeFlightPath: (id) => {
    const fp = get().data.flightPaths.find((f) => f.id === id);
    if (fp) scheduleAssetDeletion(fp.thermalVideoUrl, fp.visualVideoUrl);
    set((state) => ({
      data: { ...state.data, flightPaths: state.data.flightPaths.filter((f) => f.id !== id) },
      meta: { ...state.meta, isModified: true },
    }));
  },

  toggleFlightPathVisibility: (id) =>
    set((state) => ({
      data: {
        ...state.data,
        flightPaths: state.data.flightPaths.map((f) => (f.id === id ? { ...f, visible: !f.visible } : f)),
      },
    })),

  clearFlightPaths: () => {
    for (const fp of get().data.flightPaths) {
      scheduleAssetDeletion(fp.thermalVideoUrl, fp.visualVideoUrl);
    }
    set((state) => ({
      data: { ...state.data, flightPaths: [] },
      meta: { ...state.meta, isModified: true },
    }));
  },

  // Geo photos
  addGeoPhoto: (photo) =>
    set((state) => ({
      data: { ...state.data, geoPhotos: [...state.data.geoPhotos, photo] },
      meta: { ...state.meta, isModified: true },
    })),

  removeGeoPhoto: (id) => {
    const photo = get().data.geoPhotos.find((p) => p.id === id);
    if (photo) scheduleAssetDeletion(photo.url);
    set((state) => ({
      data: { ...state.data, geoPhotos: state.data.geoPhotos.filter((p) => p.id !== id) },
      meta: { ...state.meta, isModified: true },
    }));
  },

  updateGeoPhotoCoords: (id, coordinates) =>
    set((state) => ({
      data: {
        ...state.data,
        geoPhotos: state.data.geoPhotos.map((p) =>
          p.id === id ? { ...p, coordinates } : p,
        ),
      },
      meta: { ...state.meta, isModified: true },
    })),

  // Site layout
  //
  // F-01A: all singleton-action signatures preserved. Internally every action
  // updates `data.siteLayouts[<active>]` (the canonical shape) and refreshes
  // the deprecated `data.siteLayout` alias to match.
  // F-01B: alias now follows `state.activeLayoutId`, not always `siteLayouts[0]`.
  //
  // `setSiteLayout` legacy semantics: replaces the HEAD layout (siteLayouts[0])
  // and resets active to the new head. Used by callers that want to "swap the
  // first layout" or clear all layouts (passing `undefined`). For appending a
  // new layout without disturbing existing ones, callers should use
  // `addSiteLayoutAppend` instead (introduced in F-01B).
  setSiteLayout: (layout) => {
    // Clean up old layout assets if replacing
    const old = get().data.siteLayout;
    if (old && layout && old.id !== layout.id) {
      scheduleAssetDeletion(old.pdfAssetUrl);
      if (old.pageImage) scheduleAssetDeletion(old.pageImage.url);
    }
    set((state) => {
      const tail = state.data.siteLayouts && state.data.siteLayouts.length > 1
        ? state.data.siteLayouts.slice(1)
        : [];
      const newLayouts: SiteLayout[] | undefined = layout ? [layout, ...tail] : (tail.length > 0 ? tail : undefined);
      const newHead = newLayouts && newLayouts.length > 0 ? newLayouts[0] : undefined;
      return {
        data: {
          ...state.data,
          siteLayouts: newLayouts,
          siteLayout: newHead,
        },
        // F-01B: keep activeLayoutId aligned with the new head, or clear if
        // we just emptied the array.
        activeLayoutId: newHead ? newHead.id : null,
        meta: { ...state.meta, isModified: true },
      };
    });
  },

  // F-01B: append a new layout to siteLayouts[]; activate it; do NOT delete
  // any previously-imported layout's assets.
  addSiteLayoutAppend: (layout) => set((state) => {
    const existing = state.data.siteLayouts ?? [];
    // Collision-safe id: if the incoming id already exists, suffix a short
    // random tag rather than silently overwriting another layout.
    let safeLayout = layout;
    if (existing.some((l) => l.id === layout.id)) {
      const tag = Math.random().toString(36).slice(2, 8);
      safeLayout = { ...layout, id: `${layout.id}-${tag}` };
    }
    const newLayouts = [...existing, safeLayout];
    return {
      data: {
        ...state.data,
        siteLayouts: newLayouts,
        siteLayout: safeLayout,
      },
      activeLayoutId: safeLayout.id,
      meta: { ...state.meta, isModified: true },
    };
  }),

  // F-01B: remove a layout by id and schedule deletion of its PDF + page
  // image assets. Picks the next layout in array order as the new active
  // (else the previous, else clears the alias when none remain).
  removeSiteLayoutById: (layoutId) => {
    const state = get();
    const layouts = state.data.siteLayouts ?? [];
    const idx = layouts.findIndex((l) => l.id === layoutId);
    if (idx < 0) return; // no such layout — no-op
    const removed = layouts[idx];

    // Schedule asset deletion for the removed layout's persisted artifacts.
    // This mirrors the existing replace-singleton cleanup path.
    if (removed.pdfAssetUrl) scheduleAssetDeletion(removed.pdfAssetUrl);
    if (removed.pageImage) scheduleAssetDeletion(removed.pageImage.url);

    set((s) => {
      const next = (s.data.siteLayouts ?? []).filter((l) => l.id !== layoutId);

      // Determine new active layout deterministically.
      let newActiveId: string | null = null;
      let newAlias: SiteLayout | undefined = undefined;
      const wasActive = s.activeLayoutId === layoutId;

      if (next.length === 0) {
        newActiveId = null;
        newAlias = undefined;
      } else if (wasActive) {
        // Prefer the layout that took the removed slot (idx); if removal
        // popped the last element, pick the new last (idx - 1).
        const nextIdx = Math.min(idx, next.length - 1);
        newAlias = next[nextIdx];
        newActiveId = newAlias.id;
      } else {
        // Active wasn't removed — keep it. If the stored active id is stale
        // (somehow not in `next`), fall back to next[0] safely.
        const existing = next.find((l) => l.id === s.activeLayoutId);
        newAlias = existing ?? next[0];
        newActiveId = newAlias.id;
      }

      return {
        data: {
          ...s.data,
          siteLayouts: next.length > 0 ? next : undefined,
          siteLayout: newAlias,
        },
        activeLayoutId: newActiveId,
        meta: { ...s.meta, isModified: true },
      };
    });
  },

  // F-01B: select which layout is active. Updates the deprecated
  // `data.siteLayout` alias so existing singleton panels rebind.
  // Activation is a UI selection — NOT a project mutation. Does not set
  // `isModified` so just switching layouts won't queue a spurious save.
  setActiveLayoutId: (layoutId) => set((state) => {
    const layouts = state.data.siteLayouts ?? [];
    const target = layoutId ? layouts.find((l) => l.id === layoutId) : undefined;
    return {
      activeLayoutId: target ? target.id : null,
      data: { ...state.data, siteLayout: target },
    };
  }),

  updateSiteLayoutAnchors: (anchors, transform) => {
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => ({
        ...l,
        anchors,
        ...(transform !== undefined ? { transform } : {}),
      }));
      return patch ?? state;
    });
    // WP-A convergence: the layout's anchor transform is THE registration —
    // when this layout has a companion plan overlay (created from the same
    // PDF at import), derive the sheet's corners from the fresh transform so
    // one anchoring session positions plan + labels + DXF together. Runs on
    // every anchor placement/deletion that yields a transform; a manual
    // overlay Adjust afterwards survives until the NEXT anchor edit.
    if (transform) {
      const st = get();
      const layout = st.data.siteLayout;
      if (layout?.planOverlayId && layout.pageImage) {
        const overlay = st.data.overlays.find((o) => o.id === layout.planOverlayId);
        if (overlay) {
          st.updateOverlay(overlay.id, {
            bounds: pageImageCorners(
              layout.pageImage.pageWidthPt,
              layout.pageImage.pageHeightPt,
              transform,
            ),
          });
        }
      }
    }
  },

  updateSiteLayoutEntities: (entities, opts) =>
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => ({
        ...l,
        textEntities: entities,
        ...(opts?.reclassified ? { classifierVersion: CLASSIFIER_VERSION } : {}),
      }));
      return patch ?? state;
    }),

  updateSiteLayoutDisplay: (updates) =>
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => ({ ...l, ...updates }));
      return patch ?? state;
    }),

  setLayoutPlanOverlay: (overlayId) =>
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => ({ ...l, planOverlayId: overlayId }));
      return patch ?? state;
    }),

  // F-01A: topologyExceptions now lives at the site level. The action signature
  // is unchanged so existing UI compiles; the write target is the new site-level
  // map. Legacy per-layout exceptions are hoisted on load (see normalizeProjectLayouts).
  updateSiteLayoutException: (indicationId, override) =>
    set((state) => {
      // Guard: there must be at least one layout to attach an override to
      // (matches prior behavior of returning early when `state.data.siteLayout`
      // was absent).
      const hasLayout =
        (state.data.siteLayouts && state.data.siteLayouts.length > 0) ||
        !!state.data.siteLayout;
      if (!hasLayout) return state;
      return {
        data: {
          ...state.data,
          topologyExceptions: {
            ...(state.data.topologyExceptions ?? {}),
            [indicationId]: override,
          },
        },
        meta: { ...state.meta, isModified: true },
      };
    }),

  updateSiteLayoutConfig: (config) =>
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => ({ ...l, classificationConfig: config }));
      return patch ?? state;
    }),

  setIgnoredFamilies: (signatures) =>
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => ({ ...l, ignoredFamilies: signatures }));
      return patch ?? state;
    }),

  applyClassificationToAllLayouts: () =>
    set((state) => {
      const layouts = state.data.siteLayouts?.length
        ? state.data.siteLayouts
        : state.data.siteLayout
          ? [state.data.siteLayout]
          : [];
      if (layouts.length === 0) return state;

      const activeId = state.activeLayoutId;
      const source =
        (activeId && layouts.find((l) => l.id === activeId)) || layouts[0];
      const schemas = source.labelSchemas ?? [];
      const config = source.classificationConfig ?? [];

      const newLayouts = layouts.map((l) => ({
        ...l,
        labelSchemas: schemas,
        classificationConfig: config,
        classifierVersion: CLASSIFIER_VERSION,
        textEntities: reclassifyAndBind(l.textEntities, config),
      }));

      const activeUpdated =
        newLayouts.find((l) => l.id === source.id) ?? newLayouts[0];
      return {
        data: {
          ...state.data,
          siteLayouts: newLayouts,
          siteLayout: activeUpdated,
        },
        meta: { ...state.meta, isModified: true },
      };
    }),

  setLabelSchemas: (schemas) =>
    set((state) => {
      // Build customLevelColors from schemas that define custom level colors
      const customLevelColors: Record<string, string> = {};
      for (const s of schemas) {
        if (s.customLevelColor) {
          customLevelColors[s.level] = s.customLevelColor;
        }
      }
      const patch = applyActiveLayoutUpdate(state, (l) => ({
        ...l,
        labelSchemas: schemas.length > 0 ? schemas : undefined,
        customLevelColors: Object.keys(customLevelColors).length > 0 ? customLevelColors : undefined,
      }));
      return patch ?? state;
    }),

  addTextEntity: (entity) =>
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => ({
        ...l,
        textEntities: [...l.textEntities, entity],
      }));
      return patch ?? state;
    }),

  removeTextEntity: (entityId) =>
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => ({
        ...l,
        textEntities: l.textEntities.filter((e) => e.id !== entityId),
      }));
      return patch ?? state;
    }),

  updateTextEntity: (entityId, updates) =>
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => ({
        ...l,
        textEntities: l.textEntities.map((e) => (e.id === entityId ? { ...e, ...updates } : e)),
      }));
      return patch ?? state;
    }),

  batchUpdateTextEntities: (updates) =>
    set((state) => {
      const updateMap = new Map(updates.map((u) => [u.id, u]));
      const patch = applyActiveLayoutUpdate(state, (l) => ({
        ...l,
        textEntities: l.textEntities.map((e) => {
          const u = updateMap.get(e.id);
          if (!u) return e;
          return { ...e, cx: u.cx, cy: u.cy, ...(u.manuallyMoved !== undefined ? { manuallyMoved: u.manuallyMoved } : {}) };
        }),
      }));
      return patch ?? state;
    }),

  removeTextEntities: (entityIds) =>
    set((state) => {
      const ids = new Set(entityIds);
      const patch = applyActiveLayoutUpdate(state, (l) => ({
        ...l,
        textEntities: l.textEntities.filter((e) => !ids.has(e.id)),
      }));
      return patch ?? state;
    }),

  batchSetLabelLevel: (entityIds, level) =>
    set((state) => {
      const ids = new Set(entityIds);
      const patch = applyActiveLayoutUpdate(state, (l) => ({
        ...l,
        textEntities: l.textEntities.map((e) => {
          if (!ids.has(e.id)) return e;
          // Same shape the single-label popup writes: override + mirror into
          // classification so every consumer (halo colors, families, suggest)
          // sees the new level immediately.
          const override = { level, value: e.normalizedText };
          return { ...e, classificationOverride: override, classification: override };
        }),
      }));
      return patch ?? state;
    }),

  // Module grid
  setModuleGridConfig: (config) =>
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => ({ ...l, moduleGridConfig: config }));
      return patch ?? state;
    }),

  batchPatchStringOverrides: (entityIds, patch) =>
    set((state) => {
      const applied = applyActiveLayoutUpdate(state, (l) => {
        const overrides = { ...(l.stringOverrides ?? {}) };
        for (const id of entityIds) {
          overrides[id] = { ...overrides[id], ...patch };
        }
        return { ...l, stringOverrides: overrides };
      });
      return applied ?? state;
    }),

  batchApplyStringOverridePatches: (patches) =>
    set((state) => {
      const applied = applyActiveLayoutUpdate(state, (l) => {
        const overrides = { ...(l.stringOverrides ?? {}) };
        for (const [id, patch] of Object.entries(patches)) {
          overrides[id] = { ...overrides[id], ...patch };
        }
        return { ...l, stringOverrides: overrides };
      });
      return applied ?? state;
    }),

  batchRemoveStringOverrides: (entityIds) =>
    set((state) => {
      const applied = applyActiveLayoutUpdate(state, (l) => {
        const overrides = { ...(l.stringOverrides ?? {}) };
        for (const id of entityIds) delete overrides[id];
        return { ...l, stringOverrides: overrides };
      });
      return applied ?? state;
    }),

  setStringOverride: (entityId, override) =>
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => {
        const overrides = { ...(l.stringOverrides ?? {}) };
        if (override) {
          overrides[entityId] = override;
        } else {
          delete overrides[entityId];
        }
        return { ...l, stringOverrides: overrides };
      });
      return patch ?? state;
    }),

  removeStringOverride: (entityId) =>
    set((state) => {
      const patch = applyActiveLayoutUpdate(state, (l) => {
        const overrides = { ...(l.stringOverrides ?? {}) };
        delete overrides[entityId];
        return { ...l, stringOverrides: overrides };
      });
      return patch ?? state;
    }),

  // Date filter
  setSelectedDate: (date) => set({ selectedDate: date }),

  updateAvailableDates: () => {
    const { indications, orthophotos, flightPaths, geoPhotos, captureDates } = get().data;
    const dates = new Set<string>();

    // Capture dates are grouped by scan DAY: imported photos that stored a full
    // timestamp collapse into one row per day (not one per photo-second).
    const addDay = (v: string | null | undefined) => { const day = captureDay(v); if (day) dates.add(day); };

    // User-managed capture dates
    (captureDates || []).forEach(addDay);

    // Dates discovered from items (overlays excluded — not date-filtered)
    indications.forEach((i) => addDay(i.properties.captureDate));
    orthophotos.forEach((o) => addDay(o.captureDate));
    flightPaths.forEach((f) => addDay(f.captureDate));
    geoPhotos.forEach((p) => addDay(p.captureDate));

    const newAvailable = Array.from(dates).sort().reverse();
    // Auto-add newly discovered dates to visibleDates so items are visible by default
    const currentVisible = new Set(get().visibleDates);
    const prevAvailable = new Set(get().availableDates);
    for (const d of newAvailable) {
      if (!prevAvailable.has(d)) currentVisible.add(d);
    }
    const newVisible = Array.from(currentVisible);
    set({ availableDates: newAvailable, visibleDates: newVisible });
  },

  addCaptureDate: (date) => {
    const state = get();
    const currentDates = state.data.captureDates || [];
    if (currentDates.includes(date)) return;

    const newCaptureDates = [...currentDates, date].sort().reverse();
    const newVisibleDates = [...state.visibleDates];
    if (!newVisibleDates.includes(date)) newVisibleDates.push(date);

    set({
      data: { ...state.data, captureDates: newCaptureDates, visibleDates: newVisibleDates },
      meta: { ...state.meta, isModified: true },
      selectedDate: date,
      visibleDates: newVisibleDates,
    });
    get().updateAvailableDates();
  },

  removeCaptureDate: (date) => {
    const state = get();
    const newCaptureDates = (state.data.captureDates || []).filter((d) => d !== date);
    const newVisibleDates = state.visibleDates.filter((d) => d !== date);

    set({
      data: { ...state.data, captureDates: newCaptureDates, visibleDates: newVisibleDates },
      meta: { ...state.meta, isModified: true },
      selectedDate: state.selectedDate === date ? null : state.selectedDate,
      visibleDates: newVisibleDates,
    });
    get().updateAvailableDates();
  },

  toggleDateVisibility: (date) => {
    const state = get();
    const isVisible = state.visibleDates.includes(date);
    const newVisibleDates = isVisible
      ? state.visibleDates.filter((d) => d !== date)
      : [...state.visibleDates, date];

    set({
      visibleDates: newVisibleDates,
      data: { ...state.data, visibleDates: newVisibleDates },
    });
  },

  setAllDatesVisible: () => {
    const newVisibleDates = [...get().availableDates];
    set({
      visibleDates: newVisibleDates,
      data: { ...get().data, visibleDates: newVisibleDates },
    });
  },

  setNoDatesVisible: () => {
    set({
      visibleDates: [],
      data: { ...get().data, visibleDates: [] },
    });
  },

  // Project operations
  loadProject: (data, meta) => {
    const mergedData = { ...defaultData, ...data };
    // Migrate old videoUrl → thermalVideoUrl on flight paths
    type LegacyFlightPath = FlightPath & { videoUrl?: string };
    mergedData.flightPaths = mergedData.flightPaths.map((fp) => {
      const legacy = fp as LegacyFlightPath;
      if (legacy.videoUrl && !fp.thermalVideoUrl) {
        const { videoUrl, ...rest } = legacy;
        return { ...rest, thermalVideoUrl: videoUrl };
      }
      return fp;
    });
    // Backfill a captureDate (scan day) onto flight paths that lack one, derived
    // from the DJI-timestamped name, so imported routes participate in the date
    // filter like photos. Additive: only fills a missing value.
    mergedData.flightPaths = mergedData.flightPaths.map((fp) => {
      if (fp.captureDate) return fp;
      const ms = djiCaptureMs(fp.name);
      if (ms == null) return fp;
      const day = captureDay(new Date(ms).toISOString());
      return day ? { ...fp, captureDate: day } : fp;
    });
    // F-01A: Normalize legacy/per-layout layout shapes into the new canonical
    // `siteLayouts[] + site-level topologyExceptions` shape, and keep the
    // deprecated `siteLayout` alias in sync. Idempotent.
    const normalized = normalizeProjectLayouts(mergedData);
    // F-01B: default the active layout to the first one in the array. The
    // alias was just set to `siteLayouts[0]` by normalize, so this keeps the
    // two in lockstep from the very first frame.
    const initialActiveId = normalized.siteLayouts && normalized.siteLayouts.length > 0
      ? normalized.siteLayouts[0].id
      : null;
    set({
      data: normalized,
      meta: { ...defaultMeta, ...meta, isModified: false },
      activeLayoutId: initialActiveId,
    });
    get().updateAvailableDates();
    // Restore visibleDates from project data, or default to all dates visible
    const visibleDates = mergedData.visibleDates?.length
      ? mergedData.visibleDates
      : get().availableDates;
    set({ visibleDates });

    // Reset monitoring state before restoring — prevents stale data from previous project
    // These are set BEFORE selectSite() fires (via useEffect on siteId change),
    // and selectSite() now preserves them instead of wiping.
    const monStore = useMonitoringStore.getState();
    monStore.resetProjectState();

    // Reset review-tool stores — prevents asset nodes / evidence / observations /
    // findings / claims from a previous project leaking into the new one.
    useAssetNodeStore.getState().clear();
    useEvidenceStore.getState().clear();
    useObservationStore.getState().clear();
    useFindingStore.getState().clear();
    useClaimStore.getState().clear();
    useStateStore.getState().setSiteId(null);
    useScoringStore.getState().setSiteId(null);
    useTruthStore.getState().setSiteId(null);
    // F-02E-2B: monitoring data (config + schema + sample data) feeds Data
    // Points + alarms. Share visitors only get it when the link exposes the
    // 'monitoring' section. Skipping setProjectConfig keeps
    // monitoringStore.projectConfig at null, which collapses the legend
    // section AND starves useDataPointMarkers (defence in depth — both also
    // gate locally).
    const monitoringAllowed = isMonitoringAllowedForCurrentSession();
    if (data.monitoringConfig && monitoringAllowed) {
      monStore.setProjectConfig(data.monitoringConfig);
    }
    if (data.monitoringSchema && monitoringAllowed) {
      monStore.setSchema(data.monitoringSchema);
    }
    if (data.monitoringSampleData && monitoringAllowed) {
      monStore.setUploadedSampleData(data.monitoringSampleData);
      monStore.updateRuntimeData(
        data.monitoringSampleData,
        new Date().toISOString(),
      );
    }

    // Restore SLD diagrams if present in the project
    if (data.sldDiagrams && Array.isArray(data.sldDiagrams) && data.sldDiagrams.length > 0) {
      monStore.loadMonitoringConfig({ sldDiagrams: data.sldDiagrams });
    }

    // Restore tasks data if present in the project
    useTaskStore.getState().loadTasksConfig(data.tasksData);

    // Restore layer visibility preferences if present
    {
      const mapState = useMapStore.getState();
      const patch: Partial<MapState> = {};
      if (data.layerVisibility) {
        patch.layerVisibility = { ...mapState.layerVisibility, ...data.layerVisibility };
      }
      if (data.layoutLabelLevels) {
        patch.layoutLabelLevels = { ...mapState.layoutLabelLevels, ...data.layoutLabelLevels };
      }
      if (data.indicationFilters) {
        patch.indicationFilters = { ...mapState.indicationFilters, ...data.indicationFilters };
      }
      // F-02E-2B: don't inherit the owner's saved alarm-state filters into
      // a share viewer. Owners typically uncheck Stale + No Data once live
      // SCADA data is flowing; visitors have no monitoring runtime (the
      // backend rejects /monitoring/* for share visitors unless monitoring
      // is exposed, and even then runtime data is F-02E-2C scope), so every
      // marker defaults to state 'no_data' — applying the owner filter
      // would hide them all. Leaving the mapStore default `{}` makes the
      // checkbox-render shortcut `?? true` show every state.
      if (data.dataPointAlarmFilters && !isShareViewerActive()) {
        patch.dataPointAlarmFilters = { ...mapState.dataPointAlarmFilters, ...data.dataPointAlarmFilters };
      }
      if (typeof data.dataPointShowLabels === 'boolean') {
        patch.dataPointShowLabels = data.dataPointShowLabels;
      }
      // Show-resolved follows the project blob for everyone, share viewers
      // included — a read-only link opens looking the way the owner left it.
      // Absent in legacy blobs → reset to the default (true) so the previous
      // project's toggle doesn't leak across loads.
      patch.indicationShowResolved =
        typeof data.indicationShowResolved === 'boolean' ? data.indicationShowResolved : true;
      // Always reset IV state so stale layers from the previous project don't persist
      patch.ivSessionVisibility = data.ivSessionVisibility || {};
      // Layer master switch: saved value wins; legacy blobs (no flag) derive
      // "on" from any selected session so they render exactly as before.
      patch.ivLayerVisible =
        typeof data.ivLayerVisible === 'boolean'
          ? data.ivLayerVisible
          : Object.values(data.ivSessionVisibility || {}).some(Boolean);
      patch.ivDisplayMetric = typeof data.ivDisplayMetric === 'string' ? data.ivDisplayMetric : 'result';
      patch.ivResultFilters = data.ivResultFilters
        ? { ...mapState.ivResultFilters, ...data.ivResultFilters }
        : { pass: true, warn: true, fail: true, unknown: true };

      if (Object.keys(patch).length > 0) {
        useMapStore.setState(patch);
      }
      // If there are visible IV sessions, ensure sessions are fetched so the overlay renders.
      // setSiteId clears stale cache and calls fetchSessions(); fetchSessions then auto-loads
      // measurements for any session marked visible in mapStore.
      // F-02E-2C: share viewers without the 'iv' section must NOT trigger
      // fetchSessions — /iv/sessions returns 403 ("Section not exposed by
      // this share link") which spams the console on every viewer load.
      // We still clear the IV store via setSiteId(null) so stale cache
      // from a prior session doesn't leak across project switches; the
      // null branch in ivStore.setSiteId bails out before fetchSessions().
      const ivSiteId = isShareSectionAllowed('iv') ? (meta?.siteId ?? null) : null;
      if (data.ivSessionVisibility && Object.values(data.ivSessionVisibility).some(Boolean)) {
        useIVStore.getState().setSiteId(ivSiteId);
      } else {
        // Clear stale IV cache from previous project
        useIVStore.getState().setSiteId(ivSiteId);
      }
    }
  },

  loadProjectFromServer: async (siteId, siteName, clientName) => {
    const result = await fetchProject(siteId);
    const projectData = (result.data || {}) as unknown as ProjectData;

    // V4 Phase 2: Fetch entities from entity tables.
    // Authority rule: if site is migrated for an entity type, entity data is
    // authoritative (even if empty). Only fall back to blob on API error or
    // non-migrated site.  Fetches run in parallel for speed.
    //
    // Switched entity families (1:1 lossless mapping to frontend types):
    //   - indications (P2.4)
    //   - overlays (P2.6)
    //   - orthophotos (P2.6)
    //   - issues + tasks (V4 schema amendment: extras_json for lossless projection)
    let tasksMigrated = false;
    // F-02E-2C: share viewers without the 'tasks' section must NOT call
    // fetchEntityTasksConfig — its two requests (/issues + /tasks) return
    // 403 "Section not exposed by this share link" and spam the console
    // on every viewer load. Skip the call entirely; the existing
    // catch/fallback path already leaves tasksMigrated=false and lets the
    // blob fallback drive tasks state (which is empty for share visitors
    // without tasks exposure).
    const tasksAllowed = isShareSectionAllowed('tasks');
    try {
      const [indicationResult, overlayResult, orthophotoResult, tasksConfigResult] = await Promise.all([
        fetchEntityIndications(siteId).catch((err: unknown) => {
          console.warn('[V4] Entity indication fetch failed, using blob fallback:', err);
          return null;
        }),
        fetchEntityOverlays(siteId).catch((err: unknown) => {
          console.warn('[V4] Entity overlay fetch failed, using blob fallback:', err);
          return null;
        }),
        fetchEntityOrthophotos(siteId).catch((err: unknown) => {
          console.warn('[V4] Entity orthophoto fetch failed, using blob fallback:', err);
          return null;
        }),
        tasksAllowed
          ? fetchEntityTasksConfig(siteId).catch((err: unknown) => {
              console.warn('[V4] Entity tasks/issues fetch failed, using blob fallback:', err);
              return null;
            })
          : Promise.resolve(null),
      ]);

      if (indicationResult?.migrated) {
        projectData.indications = indicationResult.indications;
      }
      if (overlayResult?.migrated) {
        projectData.overlays = overlayResult.overlays;
      }
      if (orthophotoResult?.migrated) {
        projectData.orthophotos = orthophotoResult.orthophotos;
      }
      if (tasksConfigResult?.migrated) {
        projectData.tasksData = tasksConfigResult.config;
        tasksMigrated = true;
      }
    } catch (err) {
      // Unexpected error in Promise.all — keep all blob data
      console.warn('[V4] Entity fetch batch failed, using blob fallback:', err);
    }

    get().loadProject(projectData, {
      siteId,
      siteName,
      clientName,
      lastSaved: result.updated_at || undefined,
      version: result.version,
    });

    // Set entity authority for taskStore write switch
    if (tasksMigrated) {
      useTaskStore.getState().setEntityAuthority(siteId, true);
    } else {
      useTaskStore.getState().setEntityAuthority(null, false);
    }

    return projectData;
  },

  clearProject: () =>
    set({
      data: defaultData,
      meta: defaultMeta,
      selectedDate: null,
      availableDates: [],
      visibleDates: [],
      activeLayoutId: null, // F-01B: reset on project clear
    }),

  exportProject: () => {
    const data = get().data;
    // V4 P2.5/P2.7: Serialize only confirmed entity state. Pending entity
    // writes are replaced with pre-mutation snapshots to prevent blob
    // autosave from persisting unconfirmed optimistic mutations.
    const indications = getConfirmedIndications(data.indications);
    const overlays = getConfirmedOverlays(data.overlays);
    const orthophotos = getConfirmedOrthophotos(data.orthophotos);
    // Include monitoring data in export
    const monStore = useMonitoringStore.getState();
    // Include tasks data in export
    const taskConfig = useTaskStore.getState().getTasksConfig();
    const hasTaskData = taskConfig.issues.length > 0 || taskConfig.tasks.length > 0;
    const monExport = monStore.exportMonitoringConfig();
    // Include layer visibility preferences
    const { layerVisibility, layoutLabelLevels, indicationFilters, indicationShowResolved, dataPointAlarmFilters, dataPointShowLabels, ivSessionVisibility, ivDisplayMetric, ivResultFilters, ivLayerVisible } = useMapStore.getState();
    return {
      ...data,
      indications, // override with confirmed-only state (V4 P2.5 guard)
      overlays,    // override with confirmed-only state (V4 P2.7 guard)
      orthophotos, // override with confirmed-only state (V4 P2.7 guard)
      layerVisibility,
      layoutLabelLevels,
      indicationFilters,
      indicationShowResolved,
      dataPointAlarmFilters,
      dataPointShowLabels,
      ivSessionVisibility,
      ivDisplayMetric,
      ivResultFilters,
      ivLayerVisible,
      ...(monStore.projectConfig ? { monitoringConfig: monStore.projectConfig } : {}),
      ...(monStore.schema ? { monitoringSchema: monStore.schema } : {}),
      ...(monStore.uploadedSampleData ? { monitoringSampleData: monStore.uploadedSampleData } : {}),
      ...(monExport.sldDiagrams.length > 0 ? { sldDiagrams: monExport.sldDiagrams } : {}),
      ...(hasTaskData ? { tasksData: taskConfig } : {}),
    };
  },

  flushPendingEntityWrites: async () => {
    // Wait for every in-flight entity write to settle. The handler that
    // writes the new _version back into the store runs as part of the tracked
    // promise's chain, so when this resolves callers can rely on fresh
    // versions before issuing further PATCHes.
    let safety = 5;
    while (pendingEntityPromises.size > 0 && safety-- > 0) {
      await Promise.allSettled([...pendingEntityPromises]);
    }
  },
}));
